/**
 * SCOTTSY PDP V3 — stepped buy-flow orchestrator for the Teeinblue personaliser
 * ---------------------------------------------------------------------------
 * Loaded by snippets/scottsy-pdp-v3.liquid from any product template that
 * renders it. Everything here decorates Teeinblue's own DOM the same way
 * snippets/scottsy-product-trust.liquid does: insert sibling nodes, restyle
 * via body.sv3-active, never move or rewrite Vue-owned elements. All pieces
 * self-heal via a debounced MutationObserver, so a Teeinblue re-render (brand
 * switch, template switch) just triggers a rebuild.
 *
 * Nothing is hard-coded to one product: the steps are built from whatever
 * fields the campaign renders. The only campaign-specific data is LAYOUTS,
 * which makes the layout tiles exact for the collage campaign and falls back
 * to an approximate grid elsewhere.
 *
 * Step map (design: "Scottsy Product Page v3"):
 *   1 Choose Your Phone  → .tee-available-products (brand) + .tee-variants (model)
 *   2 Customise          → .tee-customize-main-form (layout cards + photo rows)
 *   3 Complete Your Case → .scottsy-protector-upsell inside #teeFormActions
 *
 * Every step starts open and STAYS open. Nothing collapses on its own — the
 * customer can fold a finished step by clicking its header if they want the
 * room, and that click is the only thing that ever moves.
 *
 * A note on field targeting: Teeinblue names most campaign fields after their
 * artwork layer (.tee-field--layer-812530), NOT after the control they render.
 * Anything that styles a control must therefore key off what the field
 * CONTAINS (a .tee-clipart-color, a native <select>) rather than its class,
 * or it silently fails to match on most campaigns.
 */
(function () {
  'use strict';

  /* Two lines per pill: the maker as a small eyebrow, the series large under
     it — "Apple / iPhone", per Scott's mock. Keyed by Teeinblue's full label. */
  var BRAND_TWO = {
    'iPhone Case':          { brand: 'Apple',   name: 'iPhone' },
    'Samsung S Series Case': { brand: 'Samsung', name: 'S Series' },
    'Samsung A Series Case': { brand: 'Samsung', name: 'A Series' },
    'Pixel Case':           { brand: 'Google',  name: 'Pixel' }
  };

  /* Print geometry per template, as [x, y, w, h] fractions of the case back.
     Measured off the Teeinblue artwork SVG (mask rect + layer translate)
     rather than guessed, so each tile mirrors the layout the customer gets.
     Keyed on the option label; an unlisted template falls back to a plain
     two-column grid, so a new campaign never breaks the picker. */
  var LAYOUTS = {
    '1 Photo':          [[0, 0, 1, 1]],
    '2 Photos':         [[0, 0, 1, .5], [0, .5, 1, .5]],
    '3 Photos Style A': [[0, 0, 1, .333], [0, .333, 1, .334], [0, .667, 1, .333]],
    '3 Photos Style B': [[0, 0, .5, .5], [.5, 0, .5, .5], [0, .5, 1, .5]],
    '4 Photos':         [[0, 0, .5, .5], [.5, 0, .5, .5], [.5, .5, .5, .5], [0, .5, .5, .5]],
    '5 Photos Style A': [[0, 0, .5, .255], [.5, 0, .5, .255], [0, .255, 1, .49], [0, .745, .5, .255], [.5, .745, .5, .255]],
    '5 Photos Style B': [[0, 0, .5, .25], [.5, 0, .5, .25], [0, .25, .5, .25], [.5, .25, .5, .25], [0, .5, 1, .5]],
    '5 Photos Style C': [[0, 0, 1, .27], [0, .27, .5, .47], [.5, .27, .5, .47], [0, .74, .5, .26], [.5, .74, .5, .26]],
    '6 Photos Style A': [[0, 0, .5, .255], [.5, 0, .5, .255], [0, .255, .5, .49], [.5, .255, .5, .49], [0, .745, .5, .255], [.5, .745, .5, .255]],
    '6 Photos Style B': [[0, 0, 1, .25], [0, .25, .5, .25], [.5, .25, .5, .25], [0, .5, .5, .25], [.5, .5, .5, .25], [0, .75, 1, .25]],
    '6 Photos Style C': [[0, 0, 1, .25], [0, .25, 1, .25], [0, .5, .5, .25], [.5, .5, .5, .25], [0, .75, .5, .25], [.5, .75, .5, .25]],
    '7 Photos Style A': [[0, 0, .5, .25], [.5, 0, .5, .25], [0, .25, .5, .25], [.5, .25, .5, .25], [0, .5, .5, .25], [.5, .5, .5, .25], [0, .75, 1, .25]],
    '7 Photos Style B': [[0, 0, .5, .255], [.5, 0, .5, .255], [0, .255, 1, .255], [0, .51, .5, .245], [.5, .51, .5, .245], [0, .755, .5, .245], [.5, .755, .5, .245]],
    '7 Photos Style C': [[0, 0, .5, .23], [.5, 0, .5, .23], [0, .23, .5, .265], [.5, .23, .5, .265], [0, .495, .5, .505], [.5, .495, .5, .245], [.5, .74, .5, .26]],
    '8 Photos':         [[0, 0, .5, .24], [.5, 0, .5, .24], [0, .24, .5, .26], [.5, .24, .5, .26], [0, .5, .5, .25], [.5, .5, .5, .25], [0, .75, .5, .25], [.5, .75, .5, .25]]
  };

  /* Printed alongside the "Your Photos" heading, where it is acted on. Positive
     wording: a list of things that go wrong reads as a warning and puts people
     off before they have started.
     The camera stays — it is Scottsy's, and it marks the line as guidance
     before a word of it is read. It leads the whole line rather than sitting
     after the dash, which is where it looked like a typo. */
  var PHOTO_TIP = '📸 Bright, well-lit images print sharpest.';

  var state = {
    built: false,
    added: false,
    addedTimer: null,
    options: null,      /* [{value, text, cells}] */
    frameTypeOptions: null, /* [{value, text, format, tier, colour}] */
    pickedModel: false, /* Teeinblue preselects a model, so "selected" is not "chosen" */
    addOn: null,        /* null = not answered, true = yes, false = no thanks (phone case) */
    standOn: null,       /* same, for the slate stand add-on */
    deliveryHtml: null   /* last string actually written to #sv3-delivery — see decoratePriceRow() */
  };

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  /* refresh() below calls every feature function through this rather than
     directly. Without it, one function throwing (a Teeinblue DOM shape it
     didn't expect) silently skips every function listed after it on that
     pass, and every pass after — the observer keeps re-firing, but hits the
     same throw at the same point each time (pre-launch audit, M4). Each
     call isolated here means the rest of the page keeps working regardless
     of which single feature broke, and the console says which one. */
  function safe(fn) {
    try { fn(); } catch (e) { console.error('[Scottsy]', fn.name, e); }
  }

  /* ------------------------------------------------------------------ */
  /* State reads                                                         */
  /* ------------------------------------------------------------------ */

  function getModelSelect() { return $('#product-option-dropdown'); }
  function getTemplateSelect() { return $('.tee-field--template select'); }

  function modelChosen() {
    var sel = getModelSelect();
    return !!(sel && sel.value && sel.value !== 'null');
  }
  function modelName() {
    var sel = getModelSelect();
    if (!sel || !modelChosen()) return '';
    var opt = sel.options[sel.selectedIndex];
    return opt ? opt.text : sel.value;
  }
  function layoutChosen() {
    var sel = getTemplateSelect();
    return !!(sel && sel.value && sel.value !== 'null');
  }
  function photoFields() { return $$('#teeFormInputs .tee-field--photo'); }

  function photoFieldFilled(f) {
    /* .tee-photo-placeholder present -> definitely not filled, full stop,
       checked before any of the fallbacks below get a vote. Reproduced live:
       closing the Image Cropper dialog via its own "X" (rather than Save)
       mid-upload leaves Teeinblue's OWN state stuck — "Adjust Position"
       (.tee-btn--recustomize) already rendered, but the actual photo never
       set, so .tee-photo-placeholder (the empty-state icon) is still what's
       actually on screen. The button-presence fallback below then wrongly
       called that "filled", handing the field this file's own compact
       .sv3-photo-filled sizing/labels while a bare placeholder icon sits
       inside it — the broken-looking mismatch this fix is for. This also
       correctly covers the genuine mid-upload moment (Teeinblue's own
       .tee-photo-uploading sibling renders alongside .tee-photo-placeholder,
       confirmed live elsewhere this session, not replacing it) — "not filled
       while genuinely uploading" is the same answer "Upload Photo" (not yet
       "Change Photo") already gives at that exact moment, so this doesn't
       introduce a new disagreement, it removes one. */
    if (f.querySelector('.tee-photo-placeholder')) return false;
    if (f.querySelector('.tee-photo-uploaded, .tee-photo-image')) return true;
    if (f.querySelector('.tee-photo-head img, .tee-photo canvas')) return true;
    if (f.querySelector('.tee-btn--reposition, .tee-btn--recustomize, .tee-photo-thumbnail')) return true;
    var input = f.querySelector('input[type="file"]');
    if (input && input.files && input.files.length) return true;
    return false;
  }

  /* ------------------------------------------------------------------ */
  /* Step 3 add-on — a question with two answers                         */
  /* ------------------------------------------------------------------ */
  /* The protector row is injected by snippets/scottsy-product-trust.liquid,
     which owns the cart side: on add-to-cart it reads .scottsy-protector-checkbox
     and posts the matching protector variant. That checkbox is therefore left
     in the DOM and kept authoritative — this only hides its row and puts a
     readable question in front of it, writing the answer back to the checkbox.

     Two answers rather than one tick: an unanswered checkbox is ambiguous
     (skipped, or not seen?), and a customer who does not want it has nothing to
     click, so the step never resolves and keeps reading as unfinished work. */

  var ADDON_HEAD = 'Protect Your Screen';

  function addOnPrice() {
    var p = $('.scottsy-protector-price');
    var txt = p ? (p.textContent || '').trim() : '';
    return txt || '£4.99';
  }

  /* names the model the customer actually picked — "cut for your iPhone 16 Pro"
     answers the one objection a generic protector listing leaves open */
  function addOnBody() {
    var m = modelName();
    return '9H glass, cut for your ' + (m || 'exact model') +
           '. Easy self-install — bubble-free fit kit included, on in 60 seconds.';
  }

  /* Image and review score come from the real protector product, rendered into
     #sv3-addon-data by snippets/scottsy-pdp-v3.liquid. Nothing is hardcoded
     here, so a new photo or a new review lands without touching this file. */
  function addOnData() {
    var d = $('#sv3-addon-data');
    if (!d) return { image: '', rating: 0, count: 0 };
    return {
      image: d.getAttribute('data-image') || '',
      rating: parseFloat(d.getAttribute('data-rating')) || 0,
      count: parseInt(d.getAttribute('data-count'), 10) || 0
    };
  }

  function decorateAddOn() {
    var box = $('.scottsy-protector-upsell');
    if (!box) return;
    var cb = box.querySelector('.scottsy-protector-checkbox');
    if (!cb) return;

    if (!box.querySelector('.sv3-addon')) {
      box.classList.add('sv3-addon-on');
      var d = addOnData();
      /* the rating row is omitted entirely when the product has no reviews —
         printing "0 (0)" advertises the absence */
      var rating = d.count > 0
        ? '<div class="sv3-addon__rating">' +
            '<span class="sv3-addon__stars" aria-hidden="true">★★★★★</span>' +
            '<b>' + (Math.round(d.rating * 10) / 10) + '</b>' +
            '<span class="sv3-addon__count">(' + d.count + ')</span>' +
          '</div>'
        : '';
      var media = d.image
        ? '<span class="sv3-addon__media"><img src="' + d.image + '" alt="Screen protector" loading="lazy" decoding="async"></span>'
        : '';

      var ui = el('div', 'sv3-addon',
        '<div class="sv3-addon__main">' + media +
          '<div class="sv3-addon__text">' +
            '<div class="sv3-addon__top">' +
              '<span class="sv3-addon__title">' + ADDON_HEAD + '</span>' +
              '<span class="sv3-addon__price">+' + addOnPrice() + '</span>' +
            '</div>' + rating +
            '<p class="sv3-addon__body"></p>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="sv3-addon__cta" data-sv3-addon="toggle"></button>');
      (box.querySelector('.scottsy-protector-inner') || box).appendChild(ui);

      ui.addEventListener('click', function (ev) {
        var btn = ev.target.closest ? ev.target.closest('[data-sv3-addon]') : null;
        if (!btn) return;
        /* cb.disabled is true only while the trust snippet's own
           setupProtectorOnSuccess() has an add-to-cart fetch in flight for
           this exact checkbox — writing cb.checked here during that window
           doesn't stop the in-flight request (it already captured the old
           value), it just makes the button lie about what's about to land
           in the cart. */
        if (cb.disabled) return;
        var want = !cb.checked;
        state.addOn = want;
        cb.checked = want;
        cb.dispatchEvent(new Event('change', { bubbles: true }));
        syncAddOn();
        updateState();
      });
    }
    syncAddOn();
  }

  function syncAddOn() {
    var box = $('.scottsy-protector-upsell');
    var ui = box && box.querySelector('.sv3-addon');
    if (!ui) return;
    var cb = box.querySelector('.scottsy-protector-checkbox');
    var checked = !!(cb && cb.checked);

    /* updateProtectorUpsell() in the trust snippet clears the checkbox every
       time the model changes, so the checkbox — not our own flag — is the
       source of truth for what is actually going in the cart. */
    state.addOn = checked;

    var cta = ui.querySelector('.sv3-addon__cta');
    var body = ui.querySelector('.sv3-addon__body');

    var label = checked ? '✓ Screen Protection Added' : '+ Add Screen Protection';
    if (cta.textContent !== label) cta.textContent = label;
    /* classList.toggle()/setAttribute() queue a real mutation record even when
       the value they're setting is already current — same root cause as the
       setBadge() fix elsewhere in this file, just via a different DOM API.
       Reading state back off the class/attribute first keeps this a genuine
       no-op when checked hasn't actually changed since the last pass. */
    if (cta.classList.contains('is-added') !== checked) cta.classList.toggle('is-added', checked);
    var pressed = checked ? 'true' : 'false';
    if (cta.getAttribute('aria-pressed') !== pressed) cta.setAttribute('aria-pressed', pressed);

    var copy = addOnBody();
    if (body.textContent !== copy) body.textContent = copy;

    var price = ui.querySelector('.sv3-addon__price');
    var wantPrice = '+' + addOnPrice();
    if (price && price.textContent !== wantPrice) price.textContent = wantPrice;
  }

  /* ------------------------------------------------------------------ */
  /* Slate stand add-on — same card shape as the screen protector above    */
  /* (reuses its .sv3-addon* classes directly), but a genuinely separate   */
  /* mechanism, not a second call into decorateAddOn(): that path is       */
  /* hardcoded to the protector product, to isPhoneCase, and to a          */
  /* per-model variant map this doesn't need — the stand is one single-    */
  /* variant product on a different template entirely. Both the checkbox   */
  /* and the cart-add are built fresh here, unlike the protector, which    */
  /* only decorates a checkbox scottsy-product-trust.liquid already owns — */
  /* there is no equivalent raw element for the stand to decorate, so this */
  /* function is both layers at once. Its own section HEADER used to live  */
  /* here too (a "Premium Upgrade" eyebrow) — removed now that it gets a   */
  /* real step-3 header from injectStepChrome() below, same as the         */
  /* protector's "Complete Your Case", so the two didn't stack. */
  /* ------------------------------------------------------------------ */
  function standData() {
    var d = $('#sv3-stand-data');
    if (!d) return null;
    return {
      variantId: d.getAttribute('data-variant-id') || '',
      price: d.getAttribute('data-price') || '',
      image: d.getAttribute('data-image') || '',
      rating: parseFloat(d.getAttribute('data-rating')) || 0,
      count: parseInt(d.getAttribute('data-count'), 10) || 0
    };
  }

  /* Re-anchored every pass, same idiom as relocateProtectorUpsell() below —
     and, unlike the old version, scoped to the CONFIRM area's own checkbox
     wrapper rather than an unscoped first-match .tee-checkbox-wrapper (a
     class Teeinblue reuses for other fields' own Yes/No toggles). Built
     once; the anchor call after is what makes it self-heal. */
  function injectStandAddon() {
    var data = standData();
    if (!data || !data.variantId) return;
    var wrap = formActionsCheckboxWrapper();
    if (!wrap) return;

    var box = $('.sv3-stand-addon');
    if (!box) {
      var rating = data.count > 0
        ? '<div class="sv3-addon__rating"><span class="sv3-addon__stars" aria-hidden="true">★★★★★</span>' +
            '<b>' + (Math.round(data.rating * 10) / 10) + '</b>' +
            '<span class="sv3-addon__count">(' + data.count + ')</span></div>'
        : '';
      var media = data.image
        ? '<span class="sv3-addon__media"><img src="' + data.image + '" alt="Premium Matte Black Iron Display Stand" loading="lazy" decoding="async"></span>'
        : '';

      box = el('div', 'sv3-stand-addon',
        '<div class="sv3-stand-addon-inner">' +
          '<div class="sv3-stand-addon-header">Upgrade Your Stand</div>' +
          '<div class="sv3-addon">' +
            '<div class="sv3-addon__main">' + media +
              '<div class="sv3-addon__text">' +
                '<div class="sv3-addon__top">' +
                  '<span class="sv3-addon__title">Premium Matte Black Iron Display Stand</span>' +
                  '<span class="sv3-addon__price">+' + data.price + '</span>' +
                '</div>' + rating +
                '<p class="sv3-addon__body">Solid, weighted matte black iron — won’t tip, won’t slide, and holds your slate at the perfect angle to catch the light. A genuine step up from the display feet included as standard.</p>' +
              '</div>' +
            '</div>' +
            '<button type="button" class="sv3-addon__cta" data-sv3-stand="toggle"></button>' +
          '</div>' +
        '</div>');

      var cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.className = 'sv3-stand-checkbox';
      cb.hidden = true;
      box.appendChild(cb);

      box.addEventListener('click', function (ev) {
        var btn = ev.target.closest ? ev.target.closest('[data-sv3-stand]') : null;
        if (!btn) return;
        /* cb.disabled is true only while setupStandOnSuccess() has its own
           add-to-cart fetch in flight — same reasoning as the protector's
           identical guard above. */
        if (cb.disabled) return;
        cb.checked = !cb.checked;
        syncStandAddon();
        updateState();
      });
    }

    if (box.nextElementSibling !== wrap) wrap.insertAdjacentElement('beforebegin', box);
    syncStandAddon();
  }

  function syncStandAddon() {
    var wrap = $('.sv3-stand-addon');
    if (!wrap) return;
    var cb = wrap.querySelector('.sv3-stand-checkbox');
    var inner = wrap.querySelector('.sv3-stand-addon-inner');
    var cta = wrap.querySelector('.sv3-addon__cta');
    if (!cta) return;
    var checked = !!(cb && cb.checked);
    state.standOn = checked;
    var label = checked ? '✓ Premium Stand Added' : '+ Add Premium Iron Stand';
    if (cta.textContent !== label) cta.textContent = label;
    /* Same guard as syncAddOn() above — classList.toggle()/setAttribute() both
       mutate-record unconditionally, even at their current value. */
    if (cta.classList.contains('is-added') !== checked) cta.classList.toggle('is-added', checked);
    var pressed = checked ? 'true' : 'false';
    if (cta.getAttribute('aria-pressed') !== pressed) cta.setAttribute('aria-pressed', pressed);
    if (inner && inner.classList.contains('sv3-stand-addon-inner--added') !== checked) {
      inner.classList.toggle('sv3-stand-addon-inner--added', checked);
    }
  }

  /* Same event, same /cart/add.js call as setupProtectorOnSuccess() in the
     trust snippet — fired after Teeinblue's own add-to-cart succeeds, so the
     stand only ever joins a cart that already has a real order in it, never
     added on its own. No model mapping to resolve here, just the one
     variant id rendered into #sv3-stand-data. */
  function setupStandOnSuccess() {
    if (window._scottsyStandListening) return;
    window._scottsyStandListening = true;

    document.addEventListener('teeinblue-event-after-cart-added', function () {
      var wrap = $('.sv3-stand-addon');
      if (!wrap) return;
      var cb = wrap.querySelector('.sv3-stand-checkbox');
      /* cb.disabled too, not just checked — mirrors the click handler's own
         guard above. Without it, a duplicated teeinblue-event-after-cart-added
         while THIS handler's own fetch is still in flight (checked is only
         reset in the .then()/.catch() below, after the response comes back)
         would pass this check a second time and add a second stand
         (pre-launch audit, M1). */
      if (!cb || !cb.checked || cb.disabled) return;
      var data = standData();
      if (!data || !data.variantId) return;

      cb.disabled = true;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: parseInt(data.variantId, 10), quantity: 1 }] })
      })
      .then(function (res) {
        /* fetch() only rejects on a network failure — a real Shopify cart
           error (stand out of stock, a validation 4xx/5xx) still resolves
           here and was being read as success. Thrown into the same catch
           below so both failure modes get the same honest reset. */
        if (!res.ok) throw new Error('cart add failed: ' + res.status);
        cb.checked = false;
        cb.disabled = false;
        syncStandAddon();
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      })
      .catch(function (err) {
        console.error('[Scottsy] Stand add failed:', err);
        /* Reset to genuinely unchecked, not just re-enabled — nothing was
           added, so "✓ Premium Stand Added" must not keep showing. The
           customer can just tap it again. */
        cb.checked = false;
        cb.disabled = false;
        syncStandAddon();
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Confirmation gate — two boxes before Add to Cart                    */
  /* ------------------------------------------------------------------ */
  /* Box A is Teeinblue's OWN #confirmed input, relabelled rather than replaced.
     That is deliberate and load-bearing: #confirmed is what Teeinblue's own
     add-to-cart check reads, so reusing it means this gate can only ever ADD a
     condition on top of Teeinblue's — it can never replace or weaken it. Box B
     is ours, and asks the one question returns are actually made of.

     If Teeinblue disables the button for a reason of its own, that state is
     left alone rather than being cleared out from under it — this file
     never sets atc.disabled itself (see gateAtc()), so it has nothing of
     its own to release there. */

  var CONFIRM_A_DEFAULT = 'I confirm I have checked the live preview above, and I acknowledge that my case will be created as shown in the above image.';
  var CONFIRM_B_DEFAULT = 'I’ve selected the correct phone model for my device: ';
  var STEP1_HEADING_DEFAULT = 'Choose Your Phone';
  var MODEL_PLACEHOLDER_DEFAULT = 'Select your phone model…';
  /* Slate's own step1_heading is "Choose Your Size" — right for every slate
     that actually has two, wrong for the handful (heart slates today) built
     with only one: there's nothing to CHOOSE, but the size is still real
     information worth showing (Scott: "even though they cant choose, i
     think they need a step 1 showing the size") — so this reframes it as a
     statement rather than an instruction, and the field itself is left
     visible instead of hidden, showing Teeinblue's own single tile with the
     real dimension on it. Checked live rather than per-product in Liquid:
     Size is Teeinblue's own real radiogroup, so its option count is already
     sitting in the DOM by the time this is read, and one live check here
     covers every current and future single-size slate without a data
     attribute to keep in sync per product. */
  var STEP1_HEADING_SINGLE_SIZE_FALLBACK = 'Your Size';

  /* #sv3-confirm-copy (rendered by scottsy-pdp-v3.liquid from a
     product.template_suffix case block) overrides copy that assumes a phone
     model for templates that don't have one — a slate, a print, a frame.
     Teeinblue's #product-option-dropdown / .tee-variants are generic "first
     variant field" elements it reuses for whatever a campaign's first choice
     actually is (confirmed live: a photo frame's "Number of kids" dropdown
     sits in the exact same element a phone case's model dropdown does), so
     there is no way to tell "this is a phone" from the DOM — only Liquid
     knows the template. Absent for any template left unmapped, so this falls
     back to the shared phone-case default. confirm-b has no dynamic value
     appended when overridden, since only the phone-case default has a real
     per-order value (the model name) to show.

     Same signal also gates two elements that have no sensible non-phone
     wording at all, rather than a rewritten one — a case's "fit" claim and
     the iPhone/Samsung/Pixel "how to check your model" help sheet — so those
     are simply not built when the template is mapped as non-phone. */
  function isPhoneProduct() {
    return !$('#sv3-confirm-copy');
  }
  function confirmAText() {
    var data = $('#sv3-confirm-copy');
    var override = data ? data.getAttribute('data-confirm-a') : '';
    return override || CONFIRM_A_DEFAULT;
  }
  function confirmBOverride() {
    var data = $('#sv3-confirm-copy');
    return data ? data.getAttribute('data-confirm-b') : '';
  }

  /* On frame products the confirm-b claim itself changes with Format —
     "I've selected the correct size and format" doesn't cover what Print
     Only and Digital Download each need spelled out. Print/Digital text
     lives in Liquid like every other override here (data-confirm-b-print/
     -digital); Framed keeps the template's own confirmBOverride() above. */
  function frameConfirmBText() {
    var cur = currentFrameSelection();
    if (!cur || cur.format === 'framed') {
      /* Framed itself needs no second confirmation (Scott: "the second
         checkbox isn't need[ed]") — nothing specific left to warn about
         once the live-preview check (confirm-a) is done. Print Only and
         Digital Download each get their own line because there IS
         something easy to miss in those two specifically. Empty means
         buildConfirmB() removes the row entirely, not "show a blank box". */
      return '';
    }
    var data = $('#sv3-confirm-copy');
    return (data && data.getAttribute('data-confirm-b-' + cur.format)) || '';
  }
  /* The plain-radio Size field styled in section 4b2 of the CSS — slate
     today, but matched on shape (.tee-option--size[display-type="radio"]),
     not on template, so anything else built the same way is covered too. */
  function sizeRadioGroup() {
    return $('.tee-variants .tee-option--size[display-type="radio"]');
  }
  function sizeOptionCount() {
    var group = sizeRadioGroup();
    return group ? $$('.tee-radio', group).length : 0;
  }
  /* modelChosen()/modelName() below only ever read #product-option-dropdown
     (a <select>), which slate's Size never is — it is a real radiogroup, so
     those two always read as "nothing chosen" for slate regardless of what
     is actually active on screen, and step 1's badge/summary silently never
     reflected a real click. These are the radiogroup equivalents, used by
     updateState() whenever sizeRadioGroup() is present. Teeinblue always
     arrives with a real option already active here (unlike the model
     dropdown, which arrives on a blank placeholder) — see
     syncRadioGroupActiveState() for why .active/.is-checked, not
     input.checked, is the reliable signal — so "chosen" is simply "is
     something active", with no extra "did the customer actually touch it"
     gating needed the way modelChosen() + state.pickedModel provide for a
     field that can genuinely start blank. */
  function sizeChosen() {
    var group = sizeRadioGroup();
    return !!(group && $('.tee-radio.active, .tee-radio.is-checked', group));
  }
  function sizeName() {
    var group = sizeRadioGroup();
    if (!group) return '';
    var active = $('.tee-radio.active .tee-radio-label span:first-child, .tee-radio.is-checked .tee-radio-label span:first-child', group);
    return active ? active.textContent.trim() : '';
  }

  function step1HeadingText() {
    var data = $('#sv3-confirm-copy');
    var override = data ? data.getAttribute('data-step1-heading') : '';
    if (override === 'Choose Your Size' && sizeRadioGroup() && sizeOptionCount() <= 1) {
      return STEP1_HEADING_SINGLE_SIZE_FALLBACK;
    }
    return override || STEP1_HEADING_DEFAULT;
  }
  /* Unlike the other three, a mapped-but-non-phone template (photo-frame)
     leaves data-model-placeholder unset ON PURPOSE — there's no one generic
     placeholder that fits every campaign's actual first field ("Number of
     kids" on one design, something else on the next), so the right behaviour
     is to leave Teeinblue's own native placeholder text alone rather than
     replace it with anything invented here. Only an unmapped template (the
     div itself absent) falls back to the phone-case default. */
  function modelPlaceholderText() {
    var data = $('#sv3-confirm-copy');
    if (!data) return MODEL_PLACEHOLDER_DEFAULT;
    return data.getAttribute('data-model-placeholder') || '';
  }

  /* .tee-checkbox-wrapper is not unique to the final confirm block — Teeinblue
     reuses the same class for other checkboxes elsewhere in the form (e.g. a
     layer's own "Show Text" visibility toggle), and an unscoped $() grabs
     whichever renders first in DOM order. #teeFormActions is Teeinblue's own
     fixed container for the real confirm-before-purchase checkbox + ATC
     button, so every lookup below is scoped to it rather than the page. */
  function formActionsCheckboxWrapper() {
    var fa = $('#teeFormActions');
    return fa ? $('.tee-checkbox-wrapper', fa) : null;
  }

  function relabelConfirmA() {
    var wrap = formActionsCheckboxWrapper();
    if (!wrap) return;
    var label = $('.tee-checkbox__label', wrap);
    if (!label) return;
    /* Only the first TEXT node is rewritten. scottsy-product-trust.liquid
       appends its own .scottsy-phone-note element inside this same label, and
       replacing the label wholesale would destroy it. */
    var text = confirmAText();
    for (var i = 0; i < label.childNodes.length; i++) {
      var n = label.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) {
        if (n.nodeValue !== text) n.nodeValue = text;
        return;
      }
    }
  }

  function buildConfirmB() {
    var wrap = formActionsCheckboxWrapper();
    if (!wrap) return;
    var row = $('#sv3-confirm-model');
    var dynamic = frameTypeTilesEnabled();
    var override = dynamic ? frameConfirmBText() : confirmBOverride();

    /* No second confirmation at all — not an empty one — whenever there is
       nothing specific for it to say: frame on Framed format (dynamic,
       no override), and every slate (Scott: "size is pointless" — picking
       a size is one visible tile click with its own active state right
       there on screen, not a place orders actually go wrong the way a
       phone model or a frame's fulfilment format can). Scoped to non-phone
       templates only, so an unmapped template (isPhoneProduct() true,
       #sv3-confirm-copy absent, override always '') still falls through
       to the phone-case default below rather than losing its own box.
       confirmsMet() already treats a missing row as nothing-to-confirm,
       so this can't strand Add to Cart locked. */
    if (!override && (dynamic || !isPhoneProduct())) {
      if (row) { row.remove(); gateAtc(); updateState(); }
      return;
    }

    if (!row) {
      row = el('div', 'sv3-confirm');
      row.id = 'sv3-confirm-model';
      row.innerHTML =
        '<input type="checkbox" class="sv3-confirm__box" id="sv3-confirm-model-input">' +
        '<label class="sv3-confirm__label" for="sv3-confirm-model-input">' +
          (override || (CONFIRM_B_DEFAULT + '<b class="sv3-confirm__model"></b>')) +
        '</label>';
      row.querySelector('.sv3-confirm__box').addEventListener('change', function () {
        gateAtc();
        updateState();
      });
    } else if (dynamic) {
      /* Unlike the phone-case path below, which only ever patches the
         model-name placeholder, the frame claim itself can change — so
         when it does, re-write the label and make the customer confirm it
         again rather than leaving an old tick standing for a new promise. */
      var label = row.querySelector('.sv3-confirm__label');
      if (label && override && label.textContent.trim() !== override) {
        label.textContent = override;
        var box = row.querySelector('.sv3-confirm__box');
        if (box && box.checked) {
          box.checked = false;
          gateAtc();
          updateState();
        }
      }
    }
    if (wrap.nextElementSibling !== row) wrap.insertAdjacentElement('afterend', row);
    if (!dynamic && !override) {
      var out = row.querySelector('.sv3-confirm__model');
      var name = modelName() || 'no model selected yet';
      /* Unchecks on a genuine model change, same as the dynamic/frame branch
         above does for its own claim text — otherwise this only ever
         patched the bold name in place, leaving a tick ticked for "iPhone
         15" silently re-pointed at "iPhone 16" the customer never actually
         confirmed (pre-launch audit, M2). Guarded on the box existing and
         already being checked, so the very first render (empty text, box
         never ticked yet) doesn't uncheck a box nobody has touched. */
      if (out && out.textContent !== name) {
        out.textContent = name;
        var box = row.querySelector('.sv3-confirm__box');
        if (box && box.checked) {
          box.checked = false;
          gateAtc();
          updateState();
        }
      }
    }
  }

  function confirmsMet() {
    var a = $('#confirmed');
    var b = $('#sv3-confirm-model-input');
    /* No confirm-b row at all (frame products on Framed — see
       buildConfirmB()) means nothing to confirm there, not an unchecked
       box blocking Add to Cart forever. Every other path still requires
       it exactly as before, since b then genuinely exists. */
    return !!(a && a.checked) && (!b || !!b.checked);
  }

  function gateAtc() {
    var atc = $('#teeAtcButton');
    if (!atc) return;
    /* The button is NO LONGER DISABLED or dimmed. A greyed-out primary CTA above
       two unticked boxes reads as a broken page at the last step of a long
       form; a live button that answers a click by pointing at what is missing
       reads as a form. The class is kept purely as a state marker — the capture
       handler in bindConfirmGate() is what actually holds the gate shut. */
    /* classList.add()/remove() queue a mutation record even when the class is
       already in the state being requested — same root cause as the
       setBadge()/syncAddOn() fixes elsewhere in this file. Checking current
       state first keeps a steady-state pass (nothing the customer did
       actually changed) a genuine no-op. */
    var locked = !confirmsMet();
    if (locked !== atc.classList.contains('sv3-atc-locked')) {
      atc.classList.toggle('sv3-atc-locked', locked);
    }
    if (!locked) clearNeeds();
    /* This file never sets atc.disabled itself (confirmed: grep for
       "atc.disabled" turns up only this read and the clear-below — the
       lock above is applied via the sv3-atc-locked CLASS, not the native
       property), so there is no "our own lock" left to release here. The
       line that used to stand here (atc.disabled = false, unconditional)
       could only ever have cleared a disable Teeinblue itself applied for
       a reason of its own — directly contradicting this section's own
       stated rule, quoted at the top of this file, that only a lock WE
       applied is ever released. Removed rather than fixed-in-place: there
       is nothing for data-sv3-locked to track today. */
    atc.removeAttribute('data-sv3-locked');
  }

  /* Clicking Add to Cart should point at EVERY outstanding thing at once — the
     required fields Teeinblue owns as well as our two confirmations — rather
     than reporting them one at a time or leaving a dead button with a sentence
     under it. The mark is an outline on the field itself, which is what a
     customer reads as "this one", and it persists until the field is actually
     filled instead of flashing once and vanishing.

     outline, not border or box-shadow: theme.css blanket-kills box-shadow
     inside the personaliser, and a border would shift the layout of whatever
     it lands on. */

  /* A required field counts as outstanding when its controls are all empty.
     Photo fields are judged on whether an image has actually landed, because
     their file input reports empty even after a successful upload. */
  function incompleteRequired() {
    return $$('#teeFormInputs .tee-field').filter(function (f) {
      if (!f.querySelector('.tee-asterisk')) return false;
      var ctrls = $$('input, select, textarea', f).filter(function (i) {
        return i.type !== 'hidden';
      });
      if (!ctrls.length) return false;
      if (f.className.indexOf('tee-field--photo') > -1) {
        return !f.querySelector('img[src]:not([src=""])');
      }
      return !ctrls.some(function (i) {
        return (i.type === 'checkbox' || i.type === 'radio')
          ? i.checked
          : String(i.value || '').trim() !== '';
      });
    });
  }

  function confirmsOutstanding() {
    var a = $('#confirmed');
    var b = $('#sv3-confirm-model-input');
    var out = [];
    if (!(a && a.checked)) out.push(formActionsCheckboxWrapper());
    if (!(b && b.checked)) out.push($('#sv3-confirm-model'));
    return out.filter(Boolean);
  }

  function clearNeeds() {
    $$('.sv3-needs').forEach(function (el) { el.classList.remove('sv3-needs'); });
  }

  /* Marks everything outstanding and scrolls to the first one. Advisory only —
     see bindConfirmGate() for why this does not decide whether a click goes
     through. */
  /* A field can be required only because of an answer on the field right
     before it (Teeinblue's own "Add Text? Yes/No" unlocking a required Text
     box is the case Scott found: answer "Yes", leave the revealed box
     empty). That earlier field is never itself in `targets` — its own
     answer is valid — so scrolling straight to the empty box left the
     control the customer would actually change their mind on (the Yes/No
     toggle) scrolled out of view above it. Only relevant for a genuine
     Teeinblue field with a Teeinblue field right before it — the
     confirmation checkboxes flagIncomplete() also targets have no such
     "field that unlocked this one" concept, so they're left alone. */
  function scrollAnchorFor(target) {
    if (!target.classList || !target.classList.contains('tee-field')) return target;
    var prev = target.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('tee-field')) return prev;
    return target;
  }

  /* tagStepMembers() marks each step's own Teeinblue container with
     sv3-body-N (never individual fields inside it), so this walks up from
     an arbitrary flagged field to find which step actually owns it. null
     for the confirmation checkboxes — #teeFormActions sits outside every
     sv3-body-N container, never step-collapsed, so there is nothing to
     open for those. */
  function stepNumberFor(target) {
    var host = target && target.closest ? target.closest('[class*="sv3-body-"]') : null;
    if (!host) return null;
    var m = host.className.match(/sv3-body-(\d+)/);
    return m ? parseInt(m[1], 10) : null;
  }

  /* full review, finding #14 — the live site's own equivalent handler moves
     keyboard focus onto the field it's pointing at; this file's replacement
     only ever scrolled and highlighted. Without it, a keyboard-only or
     screen-reader shopper who submits with something outstanding sees/hears
     nothing telling them the click was blocked or where to go, since focus
     stays on the button they just activated. target itself is usually a
     wrapper (.tee-field / the confirm-checkbox wrapper divs), so this finds
     the real control inside it — falling back to making the wrapper itself
     focusable (a standard pattern for an error summary with no natural
     control to land on) if none exists. preventScroll, not the file's usual
     scroll-position-restore fallback for it: scrollToEl() below already
     owns the smooth scroll, a second native scroll-into-view from .focus()
     would fight it. */
  function focusAnchor(target) {
    if (!target) return;
    try {
      var focusable = (target.matches && target.matches('input, select, textarea, button, [tabindex]'))
        ? target
        : (target.querySelector && target.querySelector('input, select, textarea, button, [tabindex]'));
      if (!focusable) {
        focusable = target;
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      }
      focusable.focus({ preventScroll: true });
    } catch (e) {}
  }

  function flagIncomplete() {
    clearNeeds();
    var targets = incompleteRequired().concat(confirmsOutstanding());
    if (!targets.length) return;
    targets.forEach(function (t) { t.classList.add('sv3-needs'); });
    var anchor = scrollAnchorFor(targets[0]);
    /* Open the step first — a folded step is plain display:none (no
       transition to wait out, confirmed in the CSS), so the anchor's real
       position is available the instant this returns. Without it, the
       scroll below could land on a field still hidden inside a collapsed
       step, i.e. on blank space (pre-launch audit, M5). */
    var stepNum = stepNumberFor(anchor);
    if (stepNum) ensureStepOpen(stepNum);
    scrollToEl(anchor);
    focusAnchor(anchor);
  }

  function bindConfirmGate() {
    var atc = $('#teeAtcButton');
    if (!atc) return;

    var host = atc.parentNode;
    if (host && !host.getAttribute('data-sv3-gate-bound')) {
      host.setAttribute('data-sv3-gate-bound', '1');
      /* CAPTURE phase on the parent, so this runs before Teeinblue's own click
         handler on the button and can stop the submit cleanly.

         Note what does and does not decide the outcome. Marking the fields is
         advisory and always runs. Blocking the click is decided ONLY by our two
         confirmations, which we own and can read exactly. Teeinblue's required
         fields are marked but never blocked on, because incompleteRequired()
         infers completeness from the DOM — and a false positive there would
         mean a customer who HAS filled everything in cannot buy. Teeinblue's
         own validation stays the authority on Teeinblue's own fields. */
      host.addEventListener('click', function (e) {
        if (!confirmsMet()) {
          flagIncomplete();
          e.preventDefault();
          e.stopPropagation();
        } else {
          /* Marking/scrolling used to run unconditionally, before this check —
             so a click that Teeinblue was about to accept (both confirmations
             ticked) could ALSO scroll the customer away to a required field
             Teeinblue itself still considers incomplete (its own
             tee-field--invalid, not a guess of ours), landing on "your item
             was just added" and "here's something wrong" at the same time.
             Confirmed live: with both confirmations checked, the click still
             added to cart AND scrolled ~3600px to an invalid photo field —
             two contradictory signals from one click. Since this file
             deliberately never blocks on Teeinblue's own required fields
             (see the comment above), it has no business redirecting the
             customer away for them either — only clear stale marks from an
             earlier blocked attempt, nothing to point at on a click that is
             actually going through. */
          clearNeeds();
        }
      }, true);

      /* Clear a field's mark the moment it is filled, so the outline tracks
         what is outstanding now rather than what was outstanding when the
         button was last pressed. */
      var inputsHost = $('#teeFormInputs');
      if (inputsHost && !inputsHost.getAttribute('data-sv3-needs-bound')) {
        inputsHost.setAttribute('data-sv3-needs-bound', '1');
        ['input', 'change'].forEach(function (evt) {
          inputsHost.addEventListener(evt, function (ev) {
            var f = ev.target && ev.target.closest && ev.target.closest('.tee-field');
            if (f) f.classList.remove('sv3-needs');
          }, true);
        });
      }
    }

    var a = $('#confirmed');
    if (a && !a.getAttribute('data-sv3-gate-bound')) {
      a.setAttribute('data-sv3-gate-bound', '1');
      a.addEventListener('change', function () { gateAtc(); updateState(); });
    }
    /* Teeinblue's visible control is a sibling div with role=checkbox, not the
       input, and it may flip .checked without dispatching a change event. */
    var confirmWrap = formActionsCheckboxWrapper();
    var pseudo = confirmWrap ? $('.tee-checkbox__pseudo', confirmWrap) : null;
    if (pseudo && !pseudo.getAttribute('data-sv3-gate-bound')) {
      pseudo.setAttribute('data-sv3-gate-bound', '1');
      pseudo.addEventListener('click', function () { setTimeout(gateAtc, 0); });
      pseudo.addEventListener('keyup', function () { setTimeout(gateAtc, 0); });
    }
  }

  /* Teeinblue re-renders the field list as fields are added/removed (e.g. a
     toggle revealing a dependent field), and #teeFormActions — the block
     holding both confirm checkboxes and the ATC button — is Teeinblue's own
     element, not ours to position. Nothing guarantees it stays the LAST
     child of the form through one of those re-renders, so it's pinned back
     there on every pass rather than trusted to stay put on its own. */
  function pinFormActionsLast() {
    var form = $('#tee-artwork-form');
    var actions = $('#teeFormActions', form);
    if (!form || !actions) return;
    if (form.lastElementChild !== actions) form.appendChild(actions);
  }

  /* scottsy-product-trust.liquid's screen-protector upsell card is inserted
     via document.querySelector('.tee-checkbox-wrapper').insertAdjacentElement
     ('beforebegin', box) — same unscoped-first-match issue as the phone note
     above, just relocated here instead of hidden, since this one is a real
     revenue feature rather than redundant text. Re-anchored every refresh,
     same pattern as buildConfirmB(). */
  function relocateProtectorUpsell() {
    var box = $('.scottsy-protector-upsell');
    var wrap = formActionsCheckboxWrapper();
    if (!box || !wrap) return;
    if (box.nextElementSibling !== wrap) wrap.insertAdjacentElement('beforebegin', box);
  }

  function decorateConfirm() {
    relabelConfirmA();
    buildConfirmB();
    relocateProtectorUpsell();
    pinFormActionsLast();
    bindConfirmGate();
    gateAtc();
  }

  /* ------------------------------------------------------------------ */
  /* Step chrome — headers only, nothing opens or closes by itself       */
  /* ------------------------------------------------------------------ */

  function stepHead(n, id, title, key) {
    var head = el('button', 'sv3-step-head is-open');
    head.type = 'button';
    head.id = id;
    head.setAttribute('data-sv3-step', n);
    head.setAttribute('data-sv3-step-key', key);
    head.setAttribute('aria-expanded', 'true');
    head.innerHTML =
      '<span class="sv3-step-badge">' + n + '</span>' +
      '<span class="sv3-step-titles">' +
        '<span class="sv3-step-title">' + title + '</span>' +
        '<span class="sv3-step-summary" data-sv3-summary="' + n + '"></span>' +
      '</span>' +
      '<span class="sv3-step-chev" aria-hidden="true"></span>';
    head.addEventListener('click', function () { toggleStep(n); });
    return head;
  }

  /* Places node directly after `anchor`, moving it if Teeinblue has since
     re-rendered the anchor and left our node stranded further up the form. */
  function placeAfter(node, anchor) {
    if (!node || !anchor) return;
    if (anchor.nextElementSibling !== node) anchor.insertAdjacentElement('afterend', node);
  }

  /* Step 3's content depends on which add-on THIS template actually offers
     — the screen protector on phone cases, or the iron stand on slates,
     never both. Centralised here so injectStepChrome() (the header),
     tagStepMembers() (folding) and updateState() (the badge) all read the
     same answer and cannot drift from one another.

     protector's own visibility check (not just presence) still matters:
     .scottsy-protector-upsell is injected once at load and stays in the
     DOM permanently, toggling display via --visible as the model changes
     (updateProtectorUpsell() in the trust snippet, keyed off
     PROTECTOR_MAP) — a model can land in the dropdown before its variant
     lands in that map (confirmed live, Pixel 11 / 11 Pro was the first
     case of it), so presence alone would keep "Complete Your Case" up for
     an offer no longer being made. */
  function step3Target(form) {
    var protector = $('.scottsy-protector-upsell', form);
    if (protector && protector.classList.contains('scottsy-protector-upsell--visible')) {
      return { key: 'addon', el: protector, heading: 'Complete Your Case', sub: 'Fitted to the model you chose.', bodyClass: 'scottsy-protector-upsell', showHeader: true };
    }
    var stand = $('.sv3-stand-addon', form);
    if (stand) {
      /* Full numbered step, same as the protector above. sub is
         deliberately '' rather than removed — Scott: "I do not want the
         line Solid Iron, not display feet, looks crap... but the box
         needs to have the same space as it does now". An empty sub-line
         still builds the same .sv3-step-sub element the CSS already
         reserves this exact space for, so the addon box doesn't jump up;
         see the .sv3-step-sub:empty rule for how the blank line still
         holds its height with no text in it. */
      return { key: 'addon', el: stand, heading: 'Complete Your Slate', sub: '', bodyClass: 'sv3-stand-addon', showHeader: true };
    }
    return null;
  }

  function injectStepChrome() {
    var form = $('#tee-artwork-form');
    if (!form) return;

    /* .tee-available-products (brand pills) only exists on products using the
       two-step brand-then-model picker. Products with a single flat model
       dropdown (no brand step) never render it, so the step-1 header used to
       silently never appear for them — fall back to anchoring on .tee-variants
       (the model dropdown itself) whenever there's no brand block. */
    var brandBlock = $('.tee-available-products', form);
    var variants = $('.tee-variants', form);
    var step1Anchor = brandBlock || variants;
    /* Always a step whenever the field exists — including a single-size
       product (heart slates today), which has nothing to CHOOSE here but
       still has a real size worth showing (Scott: "even though they cant
       choose, i think they need a step 1 showing the size"). The field
       itself is never hidden; step1HeadingText() reframes the heading as a
       statement ("Your Size") rather than an instruction for that case, and
       Teeinblue's own single tile shows the real dimension underneath it. */
    var hasModelStep = !!step1Anchor;

    var customise = $('.tee-customize-main-form', form);
    var addon = step3Target(form);

    /* Fixed for the life of the page: whether THIS product has a real
       model/size step, a customise step (always) and an add-on offer is
       decided by its own Teeinblue campaign config, which cannot change
       after load — so this numbering only ever has to resolve correctly
       once, never migrate mid-session. */
    var n = 0;
    var modelNum = hasModelStep ? ++n : 0;
    var customiseNum = customise ? ++n : 0;
    var addonNum = (addon && addon.showHeader) ? ++n : 0;

    if (modelNum && !$('#sv3-step' + modelNum, form)) {
      step1Anchor.insertAdjacentElement('beforebegin', stepHead(modelNum, 'sv3-step' + modelNum, step1HeadingText(), 'model'));
      if (isPhoneProduct()) {
        $('#sv3-step' + modelNum, form).insertAdjacentElement('afterend',
          el('div', 'sv3-step-sub sv3-body-' + modelNum, 'Made to fit your exact model — camera and buttons line up perfectly.'));
      }
    }

    /* ONE row under the MODEL dropdown: the fit confirmation on the left, the
       "how to check" link on the right. They used to be two stacked blocks,
       which spent two lines on what is really a single reassurance-plus-escape
       -hatch line. Rebuilt as one element so both halves always sit level.
       Switching brand rebuilds .tee-variants, which would otherwise leave the
       row stranded under the brand pills, so it is re-anchored on every pass.
       Neither half has a non-phone equivalent worth inventing — "fit" and a
       device-model lookup sheet are both specifically about a case on a
       phone — so the whole row is skipped for a mapped non-phone template.
       sv3-body-1 is hardcoded, not modelNum: this only ever renders when
       isPhoneProduct() is true, and a phone product's model step is always
       first, so it is always exactly 1. */
    if (variants && isPhoneProduct()) {
      var row = $('#sv3-model-row', form);
      if (!row) {
        row = el('div', 'sv3-model-row sv3-body-1');
        row.id = 'sv3-model-row';
        row.innerHTML =
          '<span class="sv3-fit" id="sv3-fit"></span>' +
          '<button type="button" class="sv3-model-help-btn" id="sv3-model-help-btn">' +
            'How to check your model</button>';
        row.querySelector('#sv3-model-help-btn')
           .addEventListener('click', function () { toggleSheet(true, 'phone'); });
      }
      placeAfter(row, variants);
    }

    /* Stable scroll target for the "Create My Case" buttons further down the
       page. A dedicated anchor rather than an id on Teeinblue's own form: that
       element already carries #tee-artwork-form, and the wrappers around it are
       rebuilt on every brand switch. Always #sv3-step1 regardless of content —
       whichever step lands at position 1 is "the start of the form". */
    var head1 = $('#sv3-step1', form);
    if (head1) {
      var anchor = $('#pdp-form');
      if (!anchor) {
        anchor = el('div', 'sv3-anchor');
        anchor.id = 'pdp-form';
        anchor.setAttribute('tabindex', '-1');
      }
      if (head1.previousElementSibling !== anchor) head1.insertAdjacentElement('beforebegin', anchor);
    }

    if (customiseNum && !$('#sv3-step' + customiseNum, form)) {
      var gap = el('div', 'sv3-step-gap');
      gap.id = 'sv3-gap' + customiseNum;
      customise.insertAdjacentElement('beforebegin', gap);
      gap.insertAdjacentElement('afterend', stepHead(customiseNum, 'sv3-step' + customiseNum, 'Customise', 'customise'));
      /* No sub-line under Customise. It narrated the customer's own progress
         back at them ("0 of 4 photos added"), which the slots below already
         show, and it changed on every upload so the heading never sat still. */
    }

    if (addon && addon.showHeader) {
      if (addonNum && !$('#sv3-step' + addonNum, form)) {
        var gapA = el('div', 'sv3-step-gap');
        gapA.id = 'sv3-gap' + addonNum;
        addon.el.insertAdjacentElement('beforebegin', gapA);
        gapA.insertAdjacentElement('afterend', stepHead(addonNum, 'sv3-step' + addonNum, addon.heading, 'addon'));
        $('#sv3-step' + addonNum, form).insertAdjacentElement('afterend',
          el('div', 'sv3-step-sub sv3-body-' + addonNum, addon.sub));
      }
    } else if (addon) {
      /* Stand: no header, but still the same plain gap every step gets
         before it — Scott: "keep the gap". */
      var priorGap = addon.el.previousElementSibling;
      if (!priorGap || !priorGap.classList.contains('sv3-step-gap')) {
        addon.el.insertAdjacentElement('beforebegin', el('div', 'sv3-step-gap'));
      }
    } else {
      /* Offer withdrawn (protector's own model→variant map gap — the stand
         has no equivalent gap in practice, but the same sweep covers it).
         Found by KEY, not by number: which number it last had depends on
         whether a model step exists for this product, and looking it up
         structurally like this is correct either way. */
      var oldHead = $('[data-sv3-step-key="addon"]', form);
      if (oldHead) {
        var oldGap = oldHead.previousElementSibling;
        if (oldGap && oldGap.classList.contains('sv3-step-gap')) oldGap.remove();
        var oldSub = oldHead.nextElementSibling;
        if (oldSub && oldSub.classList.contains('sv3-step-sub')) oldSub.remove();
        oldHead.remove();
      }
    }

    tagStepMembers();
  }

  /* Marks the Teeinblue-owned blocks that belong to each step so CSS can hide
     them if the customer folds that step. Re-applied on every refresh because
     Teeinblue replaces these nodes wholesale. Numbers are read back off the
     headers' own data-sv3-step-key (set by stepHead()) rather than passed in,
     so this can never disagree with injectStepChrome() about what number a
     step actually got. */
  function tagStepMembers() {
    var form = $('#tee-artwork-form');
    if (!form) return;
    function numFor(key) {
      var h = $('[data-sv3-step-key="' + key + '"]', form);
      return h ? h.getAttribute('data-sv3-step') : null;
    }
    var m = function (sel, n) {
      if (!n) return;
      var e = $(sel, form);
      if (e && !e.classList.contains('sv3-body-' + n)) e.classList.add('sv3-body-' + n);
    };
    var modelNum = numFor('model');
    m('.tee-available-products', modelNum);
    m('.tee-variants', modelNum);
    m('.tee-customization-warning', modelNum);
    m('.tee-customize-main-form', numFor('customise'));
    var addon = step3Target(form);
    if (addon) m('.' + addon.bodyClass, numFor('addon'));
  }

  /* The ONLY thing that ever opens or closes a step. Each step is independent,
     so folding one never disturbs another — and nothing calls this except a
     click on that step's own header. */
  function toggleStep(n) {
    var form = $('#tee-artwork-form');
    if (!form) return;
    var closed = form.classList.toggle('sv3-collapsed-' + n);
    var head = $('#sv3-step' + n, form);
    if (head) {
      head.classList.toggle('is-open', !closed);
      head.setAttribute('aria-expanded', closed ? 'false' : 'true');
    }
  }

  function ensureStepOpen(n) {
    var form = $('#tee-artwork-form');
    if (form && form.classList.contains('sv3-collapsed-' + n)) toggleStep(n);
  }

  /* ------------------------------------------------------------------ */
  /* Brand pills + model dropdown polish                                 */
  /* ------------------------------------------------------------------ */

  /* Selected state for the brand pills — since the label spans now carry
     inline colour (see shortenBrandLabels()), the CSS rule that used to
     recolour a selected pill's name on :checked can no longer win over it.
     Re-run on every refresh, same as the labels themselves. */
  function syncBrandSelection() {
    $$('.tee-available-products .tee-radio-label').forEach(function (label) {
      var name = label.querySelector('.sv3-brand-name');
      if (!name) return;
      var input = label.previousElementSibling;
      var checked = !!(input && input.matches && input.matches('input') && input.checked) ||
                    !!label.closest('.tee-radio.is-checked');
      name.style.setProperty('color', checked ? '#132581' : '#1a2142', 'important');
    });
  }

  var brandClickBound = false;
  function bindBrandClick() {
    if (brandClickBound) return;
    var host = $('.tee-available-products');
    if (!host) return;
    brandClickBound = true;
    /* immediate feedback on click, rather than waiting for the debounced
       refresh cycle to notice the new :checked state */
    host.addEventListener('click', function () { setTimeout(syncBrandSelection, 0); });
  }

  function shortenBrandLabels() {
    $$('.tee-available-products .tee-radio-label span').forEach(function (span) {
      /* idempotent across re-renders: a rebuilt span carries the full label
         again; one we already rewrote carries the marker */
      if (span.querySelector && span.querySelector('.sv3-brand-name')) return;
      var full = (span.textContent || '').trim();
      var two = BRAND_TWO[full];
      if (!two) return;
      span.title = full;
      span.innerHTML =
        '<span class="sv3-brand-eyebrow">' + two.brand + '</span>' +
        '<span class="sv3-brand-name">' + two.name + '</span>';
      /* INLINE, not left to the stylesheet. Both spans matched their CSS rule
         (confirmed with element.matches()) and the rule carried !important,
         yet computed style stayed at Teeinblue's 12.5px regardless — a
         from-scratch <style> block with the identical selector showed the
         same failure to apply, while an inline !important took immediately.
         Given the page's history of cascade surprises, inline wins outright
         rather than costing another round hunting the exact rule that beat
         it. */
      var eyebrow = span.querySelector('.sv3-brand-eyebrow');
      var name = span.querySelector('.sv3-brand-name');
      if (eyebrow) eyebrow.style.cssText =
        'display:block;font-size:11px!important;font-weight:700!important;' +
        'letter-spacing:.09em;text-transform:uppercase;color:#5c668f!important;line-height:1.2';
      if (name) name.style.cssText =
        'display:block;font-size:15px!important;font-weight:800!important;' +
        'color:#1a2142!important;line-height:1.2';
    });
  }

  /* ------------------------------------------------------------------ */
  /* Radio-style button groups — Yes/No toggles, brand pills, etc.       */
  /* ------------------------------------------------------------------ */

  /* Teeinblue's .active class (border + tint) does not reliably track which
     input is actually checked, and it turns out input.checked itself is not
     always trustworthy either. Confirmed three distinct ways live: (1) on a
     field nobody had touched, "No" carried .active while both its own input
     and its sibling's were unchecked — Teeinblue silently defaults its
     internal value to one option without reflecting that as a real answer.
     (2) clicking that same "No" for real DOES set input.checked, but .active
     still never appears — Teeinblue's own value was already "No" internally,
     so from its side nothing changed and no re-render fires. (3) worse, on a
     "Background Colour Style" field defaulting to Gradient — with the live
     preview genuinely rendering a gradient — NEITHER option's input.checked
     was ever true, so checked-state alone can't rebuild the right answer
     here at all. What IS reliable: Teeinblue only renders its own (CSS-
     hidden) checkmark SVG inside the option that is genuinely current, even
     when it declines to also flip .checked or .active. So the real value is
     input.checked OR that checkmark's presence — whichever fires. Runs every
     refresh, resyncing .active/.is-checked to that combined signal: present
     on the genuinely selected pill, absent from every other, regardless of
     what Teeinblue's own class list currently says. .is-checked is included
     alongside .active since .tee-available-products uses that convention in
     places instead. */
  function syncRadioGroupActiveState() {
    $$('[role="radiogroup"]').forEach(function (group) {
      var radios = group.querySelectorAll('.tee-radio');
      if (!radios.length) return;
      /* input.checked wins whenever any input in the group is genuinely
         checked — that only happens from a real click, so it is the most
         trustworthy signal available and must not be second-guessed by a
         stale checkmark left over from Teeinblue's initial render. The
         checkmark is consulted only when NO input is checked at all, to
         recover Teeinblue's un-reflected default (case 3 above). */
      var checked = null;
      Array.prototype.forEach.call(radios, function (r) {
        var input = r.querySelector('input');
        if (input && input.checked) checked = r;
      });
      if (!checked) {
        Array.prototype.forEach.call(radios, function (r) {
          if (r.querySelector('.tee-clipart-checkmark')) checked = r;
        });
      }
      radios.forEach(function (r) {
        r.classList.toggle('active', r === checked);
        r.classList.toggle('is-checked', r === checked);
      });
    });
  }

  function polishModelSelect() {
    var sel = getModelSelect();
    if (!sel) return;
    var placeholder = modelPlaceholderText();
    var first = sel.options[0];
    if (placeholder && first && first.value === 'null' && first.text.indexOf('Select your') !== 0) {
      first.text = placeholder;
    }
    var variants = $('.tee-variants');
    if (variants) variants.classList.toggle('sv3-model-chosen', modelChosen());
  }

  /* ------------------------------------------------------------------ */
  /* Frame Format / Finish / Colour tiles (photo-frame template only)   */
  /* --------------------------------------------------------------------
     Teeinblue packs frame finish + colour + fulfilment format into one
     flat 7-value <select> (getModelSelect() — "Premium Oak Frame + Free
     Digital Download"). Reskinned here as a 3-level tile hierarchy. The
     real select is always the source of truth: there is no separate
     "selected format" JS state anywhere — every tile click resolves
     immediately to one real <option> and writes it there, exactly like
     setTemplateValue() below does for the layout picker.                */

  function frameTypeTilesEnabled() {
    var data = $('#sv3-confirm-copy');
    return !!(data && data.getAttribute('data-frame-type-tiles') === 'true');
  }

  function frameSwatchUrl(tier, colour) {
    var data = $('#sv3-confirm-copy');
    if (!data) return '';
    var attr = colour === 'oak' ? 'data-swatch-oak' :
      (tier === 'premium' ? 'data-swatch-' + colour + '-premium' : 'data-swatch-' + colour);
    return data.getAttribute(attr) || '';
  }

  /* Classifies one live <option>'s text by substring match against the
     merchant's own naming convention, never a hardcoded value list — a
     future wording tweak or an added colour keeps working unchanged. */
  function classifyProductType(text) {
    text = String(text || '');
    if (/digital download only/i.test(text)) return { format: 'digital', tier: null, colour: null };
    if (/print only/i.test(text)) return { format: 'print', tier: null, colour: null };
    var tier = /premium/i.test(text) ? 'premium' : 'standard';
    var colour = /oak/i.test(text) ? 'oak' : /black/i.test(text) ? 'black' : /white/i.test(text) ? 'white' : null;
    return { format: 'framed', tier: tier, colour: colour };
  }

  function readFrameTypeOptions() {
    var sel = getModelSelect();
    if (!sel) return [];
    return Array.prototype.slice.call(sel.options)
      .filter(function (o) { return o.value && o.value !== 'null'; })
      .map(function (o) {
        var c = classifyProductType(o.text);
        return { value: o.value, text: o.text.trim(), format: c.format, tier: c.tier, colour: c.colour };
      });
  }

  function currentFrameSelection() {
    var sel = getModelSelect();
    if (!sel || !modelChosen()) return null;
    var opt = sel.options[sel.selectedIndex];
    return opt ? classifyProductType(opt.text) : null;
  }

  /* Finds the best live option for a desired combination, degrading
     gracefully — relax colour, then tier, then format — rather than
     resetting to a default when the exact combination is not sold for the
     currently chosen Size. Confirmed live this genuinely happens: one real
     product has no Premium White Frame on A4 though A3 has it. */
  function resolveFrameOption(opts, wantFormat, wantTier, wantColour) {
    var pool = opts.filter(function (o) { return o.format === wantFormat; });
    if (!pool.length) pool = opts;
    if (wantFormat === 'framed') {
      var tierPool = pool.filter(function (o) { return o.tier === wantTier; });
      if (!tierPool.length) tierPool = pool;
      var exact = tierPool.filter(function (o) { return o.colour === wantColour; });
      pool = exact.length ? exact : tierPool;
    }
    return pool[0] || null;
  }

  function setProductTypeValue(value) {
    var sel = getModelSelect();
    if (!sel || sel.value === value) return;
    sel.value = value;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    syncFrameTypeTiles();
  }

  function clickFrameFormat(format, opts) {
    var cur = currentFrameSelection();
    var wantTier = (cur && cur.format === 'framed') ? cur.tier : 'standard';
    var wantColour = (cur && cur.format === 'framed') ? cur.colour : null;
    var m = resolveFrameOption(opts, format, wantTier, wantColour);
    if (m) setProductTypeValue(m.value);
  }

  var FRAME_FORMAT_LABELS = { framed: 'Framed Print', print: 'Print Only', digital: 'Digital' };
  var FRAME_FORMAT_SUBTITLES = { framed: 'ready to hang', print: 'fits any frame', digital: 'instant download' };
  /* Standard reuses scottsy-v3-questions.liquid qa9's own "slim matte frame"
     verbatim; Premium is Scott's own wording (thicker + woodgrain, not just
     the deeper finish qa9 describes). No separate Finish step any more
     (Scott: "if someone chooses framed - it should just show all options")
     — every framed option is its own tile, finish folded into its title. */
  var FRAME_TIER_SUBTITLES = { standard: 'slim matte frame', premium: 'thicker, woodgrain finish' };
  var FRAME_COLOUR_LABELS = { black: 'Black', white: 'White', oak: 'Oak' };
  /* ISO 216 paper sizes — universal facts, not Scottsy-specific claims. */
  var FRAME_SIZE_DIMENSIONS = { A4: '21 × 29.7 cm', A3: '29.7 × 42 cm' };

  function frameTile(cls, title, subtitle, dataAttr, dataVal, swatchUrl, mostPopular) {
    var tile = el('button', ('sv3-frame-tile ' + cls).trim());
    tile.type = 'button';
    tile.setAttribute('role', 'radio');
    tile.setAttribute('aria-checked', 'false');
    tile.setAttribute(dataAttr, dataVal);
    if (mostPopular) tile.appendChild(el('span', 'sv3-frame-badge', 'Most Popular'));
    if (swatchUrl) {
      var swatch = el('span', 'sv3-frame-swatch');
      swatch.style.backgroundImage = 'url(' + swatchUrl + ')';
      tile.appendChild(swatch);
    }
    var text = el('span', 'sv3-frame-tile__text');
    text.appendChild(el('span', 'sv3-frame-tile__label', title));
    if (subtitle) text.appendChild(el('span', 'sv3-frame-tile__sub', subtitle));
    tile.appendChild(text);
    return tile;
  }

  /* Size tiles are a CSS-only reskin of Teeinblue's real .tee-radio-label
     markup (see the CSS notes on why), so their two-line look is added the
     same way shortenBrandLabels() adds the brand pill's second line: by
     writing a new span into the real label, idempotently across re-renders.
     insertAdjacentElement('afterend', ...) on the title span specifically
     (not appendChild on the label) so the dimension always lands right
     after the size text regardless of whether injectMostPopular() (in
     scottsy-product-trust.liquid) has already appended its badge span. */
  function addSizeDimensions() {
    if (!frameTypeTilesEnabled()) return;
    $$('.tee-variants .tee-option--color .tee-radio-label').forEach(function (label) {
      if (label.querySelector('.sv3-size-dim')) return;
      var titleSpan = label.querySelector('span');
      if (!titleSpan) return;
      var dim = FRAME_SIZE_DIMENSIONS[(titleSpan.textContent || '').trim()];
      if (!dim) return;
      titleSpan.insertAdjacentElement('afterend', el('span', 'sv3-size-dim', dim));
    });
  }

  /* "See The Difference Between Our Frames" — opens the same bottom sheet
     as the phone-case "How to check your model" link (toggleSheet(),
     bindSheet()), with its title/content swapped to the frame comparison
     image for this template by scottsy-pdp-v3.liquid. Built once and moved
     into place right under the Frame Type tiles (Scott: sits between the
     colour swatches and What's Included, not after the whole card);
     visibility (Framed only — Standard/Premium has nothing to compare on
     Print Only or Digital Download) is toggled from syncFrameTypeTiles()
     below, same as the colour group itself. */
  function ensureFrameCompareBtn(wrap) {
    var btn = $('.sv3-frame-compare-btn', wrap);
    if (btn) return btn;
    btn = el('button', 'sv3-frame-compare-btn', 'See The Difference Between Our Frames');
    btn.type = 'button';
    btn.addEventListener('click', function () { toggleSheet(true, 'frame'); });
    var colourGroup = $('.sv3-frame-group--colour', wrap);
    if (colourGroup) colourGroup.insertAdjacentElement('afterend', btn); else wrap.appendChild(btn);
    return btn;
  }

  /* "Which Size Is Right For Me?" — same shared sheet as the frame/GPX/phone
     triggers above (toggleSheet(true, 'sizeHelp')), placed under the Size
     tiles styled in CSS section 4b2. Only appears when there is an actual
     choice to make: on the single-size slates sizeOptionCount() already
     gates step1HeadingText() with, a "which size" link next to one tile
     would be answering a question nobody has. */
  function ensureSizeHelpBtn() {
    var group = sizeRadioGroup();
    if (!group || sizeOptionCount() <= 1) {
      var existing = $('.sv3-size-help-btn');
      if (existing) existing.remove();
      return;
    }
    if ($('.sv3-size-help-btn')) return;
    var btn = el('button', 'sv3-size-help-btn', 'Which Size Is Right For Me?');
    btn.type = 'button';
    btn.addEventListener('click', function () { toggleSheet(true, 'sizeHelp'); });
    group.insertAdjacentElement('afterend', btn);
  }

  /* Keyrings confirmed live: sizeRadioGroup() (Teeinblue's native "Size"
     field) only ever contains ONE .tee-radio — the currently active value —
     not all three shapes, unlike slate's Size field which lists every real
     option. The actual visible, clickable choice for keyrings is Teeinblue's
     own "Available Products" tile row (.tee-available-products — the same
     element the phone brand pills use), which sizeOptionCount() knows
     nothing about. Without this check, ensureSingleSizeNote() below reads
     Size's own 1-radio group and wrongly claims "one size only" directly
     under three visible, clickable shape tiles. */
  function hasAvailableProductsChoice() {
    /* Not nested inside .tee-variants — confirmed live its actual parent is
       .tee-campaign-v1 .tee-customization-wrapper, a sibling structure, not
       a child of it (matching how the CSS itself scopes .tee-available-
       products bare, not .tee-variants .tee-available-products). */
    var ap = $('.tee-available-products');
    return !!ap && $$('.tee-radio', ap).length > 1;
  }

  /* Mirrors hasAvailableProductsChoice() onto a body class so CSS can see it
     too — needed because the CSS section "4b3. single-size Size un-hide"
     (scottsy-pdp-v3.css, search ".tee-option--size[display-type=\"radio\"]
     .sr-only") deliberately reveals Teeinblue's own sr-only Size field for
     single-size products like Heart Slate, where Size genuinely is the only
     visible step-1 control. Confirmed live on a real device (keyring, mobile
     width): without this guard that same reveal also fires for keyrings,
     where Available Products already IS the visible step-1 control — Size
     then shows as a second, contradictory pill underneath it, out of sync
     with whichever shape is actually active until a real change event fires
     on Size itself. Same underlying cause as ensureSingleSizeNote()'s guard
     above, just needed in CSS as well since that un-hide rule runs before
     any JS-built note element exists to hook a :has() off. */
  function syncAvailableProductsChoiceClass() {
    document.body.classList.toggle('sv3-ap-choice', hasAvailableProductsChoice());
  }

  /* The single-size mirror of ensureSizeHelpBtn() above — same anchor,
     mutually exclusive gate (count === 1 here, > 1 there), so exactly one
     of the two ever shows. Scott: "a small line of text saying heart
     slates available in this size only" — reframed as a plain statement
     rather than a limitation, and it proactively answers the question a
     visible single tile with nothing to click otherwise invites ("is that
     really it?"). Guarded against hasAvailableProductsChoice() (see its own
     comment) so it never contradicts a real, visible Available Products
     choice sitting right above it. */
  function ensureSingleSizeNote() {
    var group = sizeRadioGroup();
    if (!group || sizeOptionCount() !== 1 || hasAvailableProductsChoice()) {
      var existing = $('.sv3-single-size-note');
      if (existing) existing.remove();
      return;
    }
    if ($('.sv3-single-size-note')) return;
    var note = el('p', 'sv3-single-size-note', 'This design comes in one size only — nothing else to choose here.');
    group.insertAdjacentElement('afterend', note);
  }

  /* The "Which Size Is Right For Me?" sheet content — built from whatever
     sizes THIS product actually has, never hardcoded. Slate is not one
     shape: 20x20cm/15x15cm squares and 19.5x29cm/14x19cm rectangles (in
     either orientation) sit side by side in the catalogue today, and
     naming the wrong pair on a rectangle is exactly what this replaces. */

  /* Matches both formats seen live — "20x20cm" and "19cm x 14cm" (also
     the heart slate's "15x15cm (Freestanding)"; the parenthetical simply
     isn't part of the match). Returns null on anything unrecognised
     rather than guess — buildSizeHelpCards() then drops the numbers from
     its copy instead of risking a wrong one. */
  var SIZE_DIMENSION_RE = /([\d.]+)\s*(?:cm)?\s*x\s*([\d.]+)\s*cm/i;
  function parseSizeDimensions(text) {
    var m = SIZE_DIMENSION_RE.exec(text || '');
    if (!m) return null;
    var w = parseFloat(m[1]), h = parseFloat(m[2]);
    if (!w || !h) return null;
    return { area: w * h };
  }

  /* Which size to recommend is read from the REAL "Most Popular" badge
     (injectMostPopular() in scottsy-product-trust.liquid, driven by
     Scott's own CONFIG.mostPopular list — the larger size, on every slate
     product configured today) rather than re-decided here as "larger is
     always better". That keeps this sheet from ever disagreeing with the
     tile the customer is already looking at, and it keeps working exactly
     as-is if that list is ever repointed at a different size. */
  function buildSizeHelpCards() {
    var host = $('#sv3-sizehelp-cards');
    if (!host) return;
    var group = sizeRadioGroup();
    if (!group || sizeOptionCount() < 2) {
      if (host.innerHTML) { host.innerHTML = ''; host.removeAttribute('data-sv3-key'); }
      return;
    }

    var opts = $$('.tee-radio', group).map(function (r) {
      var span = $('.tee-radio-label span:first-child', r);
      var text = span ? span.textContent.trim() : '';
      return { text: text, dims: parseSizeDimensions(text), popular: !!$('.scottsy-popular-badge', r) };
    }).filter(function (o) { return o.text; });
    if (opts.length < 2) return;

    /* full review, finding #12 — key includes popular state, not just text.
       .scottsy-popular-badge is injected by a separate file
       (scottsy-product-trust.liquid's own independent 300ms poll), so it
       can land after this function's first build. Text-only used to mean
       that once cached, nothing ever invalidated it — if this ran first,
       the sheet was permanently stuck showing no size as popular even
       after the real badge became visible elsewhere on the same page. */
    var key = opts.map(function (o) { return o.text + ':' + (o.popular ? 1 : 0); }).join('|');
    if (host.getAttribute('data-sv3-key') === key) return;

    var smallest = opts[0];
    opts.forEach(function (o) { if (o.dims && (!smallest.dims || o.dims.area < smallest.dims.area)) smallest = o; });

    host.innerHTML = opts.map(function (o) {
      var heading = o.text.replace(/\s*\(.*?\)\s*$/, '');
      var desc;
      if (o.popular) {
        var extra = '';
        if (o.dims && smallest.dims && o !== smallest && smallest.dims.area > 0) {
          var pct = Math.round((o.dims.area / smallest.dims.area - 1) * 100);
          if (pct >= 90) extra = ' — more than double the space of our smallest size';
          else if (pct >= 15) extra = ' — roughly ' + (Math.round(pct / 10) * 10) + '% more space than our smallest size';
        }
        desc = 'Our most popular choice' + extra + '. More room for extra photos, a longer message, or detail that would feel cramped on a smaller size.';
      } else if (o === smallest) {
        desc = 'A neat, compact size — ideal for a single photo or a simpler design, or a smaller spot to display it.';
      } else {
        desc = 'A step up in size, with more room to work with.';
      }
      return '<div class="sv3-sheet-card"><h4>' + heading + '</h4><p>' + desc + '</p></div>';
    }).join('');
    host.setAttribute('data-sv3-key', key);
  }

  function frameGroup(cls, label) {
    var group = el('div', 'sv3-frame-group ' + cls);
    group.appendChild(el('span', 'sv3-frame-group__label', label));
    var row = el('div', 'sv3-frame-group__row');
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', label);
    group.appendChild(row);
    return group;
  }

  /* Every framed option gets its own tile — Standard Black, Premium Black,
     Premium Oak, and so on — in the live select's own order, rather than a
     Finish step that hides most of them until picked. Each tile maps 1:1 to
     a real <option>, so the click handler just writes that value directly;
     no resolving/guessing needed the way clickFrameFormat() still does. */
  /* The Colour tile's own title — Standard/Premium always stated, since a
     tile has to work as a standalone label with nothing else on the page
     carrying that distinction for it. */
  function frameColourTitle(o) {
    /* full review, finding #10 — falls back to the word "Colour", not
       o.colour itself: classifyProductType() returns colour:null for any
       Product Type text it doesn't recognise as oak/black/white (the next
       new frame colour added to the catalogue, or a reworded option), and
       string-concatenating null renders the literal word "null" in this
       tile's own label. Dormant today only because every live frame colour
       is currently recognised. */
    return (o.tier === 'premium' ? 'Premium ' : 'Standard ') + (FRAME_COLOUR_LABELS[o.colour] || 'Colour');
  }

  function buildFrameColourTiles(colourGroup, opts) {
    var row = $('.sv3-frame-group__row', colourGroup);
    opts.forEach(function (o) {
      if (o.format !== 'framed') return;
      var tile = frameTile('sv3-frame-tile--colour', frameColourTitle(o), FRAME_TIER_SUBTITLES[o.tier],
        'data-sv3-value', o.value, frameSwatchUrl(o.tier, o.colour));
      tile.addEventListener('click', function () { setProductTypeValue(o.value); });
      row.appendChild(tile);
    });
  }

  /* "What's Included" pills — Scott's own reference design stripped down
     further: no sentence, no cart-string preview, just a label and
     colour-aware chips (Scott: "really simple... pills changed based on
     selection"). The frame/colour pill names the actual colour rather
     than a generic "Frame included" — otherwise every framed combination
     showed identical pills and nothing confirmed which one was picked —
     and says "Frame & Print" specifically, not just "Frame", so it can't
     read as empty hardware (Scott: real customers have asked "do I get a
     picture with it?"). Print Only says "(Unframed)" rather than a bare
     "No Frame" pill, since a green checkmark next to a negative statement
     would visually contradict itself.

     A one-navy-pill-plus-lighter-subs hierarchy was tried here and
     reverted on request — back to a flat list of equal-weight solid
     pills, unrelated to the separate Format-tile mobile-row change
     (see the @media block near .sv3-frame-tile above). */
  function frameSelectionPills(cur) {
    if (cur.format === 'framed') {
      /* full review, finding #10 — same "Colour" fallback and same reason
         as frameColourTitle()'s own copy of this note, just above. */
      var colour = (cur.tier === 'premium' ? 'Premium ' : '') + (FRAME_COLOUR_LABELS[cur.colour] || 'Colour');
      return [colour + ' Frame & Print', 'Fully Assembled', 'Ready to Display', 'Free Digital Copy'];
    }
    if (cur.format === 'print') return ['Print Only (Unframed)', 'Postal Tube Delivery', 'Free Digital Copy'];
    return ['Instant Delivery', 'High Resolution File', 'Ready to Print'];
  }

  function buildFrameTypeTiles() {
    if (!frameTypeTilesEnabled()) return;
    var sel = getModelSelect();
    if (!sel) return;
    var field = sel.closest('.tee-option--size');
    if (!field) return;

    var opts = readFrameTypeOptions();
    /* Campaigns with only one real Product Type value get no picker — there
       is nothing to pick. Mirrors buildLayoutTiles()'s identical guard. */
    if (opts.length < 2) return;
    state.frameTypeOptions = opts;

    var key = opts.map(function (o) { return o.value; }).join('|');
    if (field.classList.contains('sv3-frame-tiles-on') && field.getAttribute('data-sv3-frame-key') === key) {
      syncFrameTypeTiles();
      return;
    }

    var old = $('.sv3-frame-tiles', field);
    if (old) old.remove();

    var wrap = el('div', 'sv3-frame-tiles');

    /* Format tiles only when more than one format is actually on offer —
       a single tile forcing a "choice" of one option is not a real choice,
       same reasoning as the Finish guard below. */
    var formats = ['framed', 'print', 'digital'].filter(function (fmt) {
      return opts.some(function (o) { return o.format === fmt; });
    });
    if (formats.length > 1) {
      var formatGroup = frameGroup('sv3-frame-group--format', 'Format');
      var formatRow = $('.sv3-frame-group__row', formatGroup);
      formats.forEach(function (fmt) {
        var tile = frameTile('', FRAME_FORMAT_LABELS[fmt], FRAME_FORMAT_SUBTITLES[fmt], 'data-sv3-format', fmt, null, false);
        tile.addEventListener('click', function () { clickFrameFormat(fmt, state.frameTypeOptions); });
        formatRow.appendChild(tile);
      });
      wrap.appendChild(formatGroup);
    }

    var hasFramed = opts.some(function (o) { return o.format === 'framed'; });
    if (hasFramed) {
      var colourGroup = frameGroup('sv3-frame-group--colour', 'Frame Type');
      buildFrameColourTiles(colourGroup, opts);
      wrap.appendChild(colourGroup);
    }

    /* "What's Included" — label + pills only now, no sentence, no cart-
       string preview (Scott, after the sentence+pills reference design:
       "make the box really simple... pills changed based on selection").
       Static skeleton built once here; syncFrameTypeTiles() fills in the
       live pills. */
    var selection = el('div', 'sv3-frame-selection');
    selection.innerHTML =
      '<p class="sv3-frame-selection__label">What’s Included With My Selection</p>' +
      '<div class="sv3-frame-selection__pills" aria-live="polite"></div>';
    wrap.appendChild(selection);

    var input = $('.tee-field__input', field) || field;
    input.insertAdjacentElement('beforebegin', wrap);
    field.classList.add('sv3-frame-tiles-on');
    field.setAttribute('data-sv3-frame-key', key);
    syncFrameTypeTiles();
  }

  function syncFrameTypeTiles() {
    var sel = getModelSelect();
    var field = sel && sel.closest('.tee-option--size');
    var wrap = field && $('.sv3-frame-tiles', field);
    if (!wrap) return;
    var cur = currentFrameSelection();
    /* immediate feedback, rather than waiting on the debounced refresh()
       that Teeinblue's own reaction to the dispatched change event would
       eventually trigger anyway via the form's MutationObserver */
    buildConfirmB();

    $$('.sv3-frame-tile[data-sv3-format]', wrap).forEach(function (t) {
      var on = !!cur && t.getAttribute('data-sv3-format') === cur.format;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
    });

    var showFramed = !!cur && cur.format === 'framed';
    var colourGroup = $('.sv3-frame-group--colour', wrap);
    if (colourGroup) colourGroup.classList.toggle('sv3-frame-group--hidden', !showFramed);
    ensureFrameCompareBtn(wrap).classList.toggle('sv3-frame-group--hidden', !showFramed);

    var selectionCard = $('.sv3-frame-selection', wrap);
    if (selectionCard) {
      selectionCard.classList.toggle('sv3-frame-group--hidden', !cur);
      if (cur) {
        var pillsWrap = $('.sv3-frame-selection__pills', selectionCard);
        if (pillsWrap) {
          var pills = frameSelectionPills(cur);
          var pillsKey = pills.join('|');
          if (pillsWrap.getAttribute('data-sv3-state') !== pillsKey) {
            pillsWrap.setAttribute('data-sv3-state', pillsKey);
            pillsWrap.textContent = '';
            pills.forEach(function (label) {
              pillsWrap.appendChild(el('span', 'sv3-frame-selection__pill', '✓ ' + label));
            });
          }
        }
      }
    }

    if (!showFramed || !colourGroup) return;

    var curValue = sel.value;
    $$('.sv3-frame-tile[data-sv3-value]', colourGroup).forEach(function (t) {
      var on = t.getAttribute('data-sv3-value') === curValue;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }

  var frameSizeClickBound = false;
  function bindFrameSizeClick() {
    if (frameSizeClickBound || !frameTypeTilesEnabled()) return;
    var host = $('.tee-variants .tee-option--color');
    if (!host) return;
    frameSizeClickBound = true;
    /* immediate feedback: Teeinblue repopulates the Product Type select's
       options as soon as Size changes (confirmed live), so a same-tick
       rebuild here beats waiting for the 150ms debounced refresh() pass —
       not load-bearing, refresh() covers it a moment later regardless. */
    host.addEventListener('click', function () { setTimeout(buildFrameTypeTiles, 0); });
  }

  /* ------------------------------------------------------------------ */
  /* Layout picker — one tile per template, drawn to real print geometry */
  /* ------------------------------------------------------------------ */

  function fallbackCells(text) {
    var m = /^(\d+)/.exec(text.trim());
    var n = m ? parseInt(m[1], 10) : 1;
    var rows = Math.ceil(n / 2), cells = [], i;
    for (i = 0; i < n; i++) {
      var lastAlone = (n % 2 === 1) && (i === n - 1);
      cells.push([lastAlone ? 0 : (i % 2) * 0.5, Math.floor(i / 2) / rows, lastAlone ? 1 : 0.5, 1 / rows]);
    }
    return cells;
  }

  function readOptions(sel) {
    return Array.prototype.slice.call(sel.options)
      .filter(function (o) { return o.value && o.value !== 'null'; })
      .map(function (o) {
        var text = o.text.trim();
        return { value: o.value, text: text, cells: LAYOUTS[text] || fallbackCells(text) };
      });
  }

  /* Phone products only — .sv3-layout-art is drawn at 572/1170, the real
     phone-case proportion (see its own CSS comment: "every option is
     unmistakably a case"), so the artwork itself asserts "this is a case"
     regardless of what the product actually is. Confirmed live on a heart-
     shaped photo collage frame: two tall narrow case-shaped tiles, visibly
     disconnected from a product that isn't case-shaped at all. Every other
     template's own .tee-field--template select is left as Teeinblue's
     native control instead — not restyled some other way, since nothing
     asked for that, just not given a phone case's own shape. */
  function buildLayoutTiles() {
    if (!isPhoneProduct()) return;
    var sel = getTemplateSelect();
    if (!sel) return;
    var field = sel.closest('.tee-field--template');
    if (!field) return;
    if (field.classList.contains('sv3-tiles-on') && $('.sv3-layouts', field)) return; /* alive */

    state.options = readOptions(sel);
    /* Campaigns with a single fixed layout (Teeinblue calls it "Default
       template" and hides the field) get no picker — there is nothing to pick. */
    if (state.options.length < 2) return;

    var old = $('.sv3-layouts', field);
    if (old) old.remove();

    var grid = el('div', 'sv3-layouts');
    state.options.forEach(function (opt) {
      var tile = el('button', 'sv3-layout-tile');
      tile.type = 'button';
      tile.setAttribute('data-sv3-value', opt.value);
      tile.setAttribute('aria-label', opt.text);
      tile.title = opt.text;
      var cells = opt.cells.map(function (c) {
        return '<i class="sv3-layout-cell" style="left:' + (c[0] * 100) + '%; top:' + (c[1] * 100) +
               '%; width:' + (c[2] * 100) + '%; height:' + (c[3] * 100) + '%"></i>';
      }).join('');
      tile.innerHTML =
        '<span class="sv3-layout-art">' + cells + '</span>' +
        '<span class="sv3-layout-label">' + opt.text + '</span>';
      tile.addEventListener('click', function () { setTemplateValue(opt.value); });
      grid.appendChild(tile);
    });

    var input = $('.tee-field__input', field) || field;
    input.insertAdjacentElement('beforebegin', grid);
    field.classList.add('sv3-tiles-on');
    syncTiles();
  }

  function setTemplateValue(value) {
    var sel = getTemplateSelect();
    if (!sel || sel.value === value) return;
    sel.value = value;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    syncTiles();
  }

  function syncTiles() {
    var sel = getTemplateSelect();
    if (!sel) return;
    var current = sel.value;
    $$('.sv3-layout-tile').forEach(function (tile) {
      tile.classList.toggle('is-active', tile.getAttribute('data-sv3-value') === current);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Photo slots                                                         */
  /* ------------------------------------------------------------------ */

  /* Teeinblue's own field heading already names the slot by its position
     ("Top Left Photo"), and photo fields now show that heading like every other
     field on the form. So the card underneath only has to say what tapping it
     does — the position name is still carried on the card as its accessible
     name, for anyone who never sees the heading. */
  var UPLOAD_LABEL = 'Upload Photo';

  function slotName(heading) {
    var t = (heading || '').replace(/[:\s]+$/, '').trim();
    if (!t || /^upload\s*photo$/i.test(t) || /^photo$/i.test(t)) return '';
    return /photo/i.test(t) ? t : (t + ' Photo');
  }

  function decoratePhotoFields() {
    var fields = photoFields();
    var inputs = $('#teeFormInputs');

    /* One slot per row, full width, in normal flow with every other field.
       The attribute is kept so the grid width stays a single CSS switch if it
       ever needs to go back to a multi-column layout. */
    if (inputs && inputs.getAttribute('data-sv3-photo-cols') !== '1') {
      inputs.setAttribute('data-sv3-photo-cols', '1');
    }

    fields.forEach(function (f, i) {
      var num = String(i + 1);
      if (f.getAttribute('data-sv3-num') !== num) f.setAttribute('data-sv3-num', num);

      var heading = f.querySelector('.tee-field__heading span');
      var raw = heading ? heading.textContent.trim() : '';
      var name = slotName(raw);
      var label = f.querySelector('.tee-photo-label');

      if (label) {
        if (label.getAttribute('data-sv3-label') !== UPLOAD_LABEL) {
          label.setAttribute('data-sv3-label', UPLOAD_LABEL);
        }
        var a11y = name || raw || ('Photo ' + num);
        if (label.getAttribute('aria-label') !== a11y) {
          label.setAttribute('aria-label', a11y);
          label.title = a11y;
        }
        if (label.getAttribute('data-sv3-num') !== num) label.setAttribute('data-sv3-num', num);
      }

      f.classList.toggle('sv3-photo-filled', photoFieldFilled(f));

      /* Teeinblue's aspect-ratio warning is deliberately not surfaced. It fires
         whenever an upload's proportions differ from the print area, which is
         almost every photo, and the crop it describes is already visible in the
         live preview — so it flagged a problem the customer could see was not
         one. The text is still on the element for anyone inspecting it. */

      placeSlotNote(f);
    });
  }

  /* A per-slot campaign instruction ("may be hidden by the camera on some
     models") is printed directly under that slot's own row, so it is
     unmistakably attached to the right photo. The wording is campaign data —
     edit it in Teeinblue, not here. */
  function placeSlotNote(field) {
    var inst = field.querySelector('.tee-upload-instruction');
    var text = inst ? inst.textContent.trim() : '';
    var note = field.querySelector('.sv3-slot-note');

    if (!text) {
      if (note) note.remove();
      field.classList.remove('sv3-photo-note');
      return;
    }
    if (!note) {
      note = el('span', 'sv3-slot-note');
    }
    if (note.textContent !== text) note.textContent = text;
    if (note.parentNode !== field || field.lastElementChild !== note) field.appendChild(note);
    field.classList.add('sv3-photo-note');
  }

  /* Step 2 runs two different jobs together — pick an arrangement, then feed it
     photos — and with the native field headings hidden they ran into each other
     as one long grey area. These two headings split it into named sections, and
     the photo guidance sits where it is acted on rather than floating. */
  function placeSectionHeads() {
    var tiles = $('.sv3-layouts');
    if (tiles) {
      var lh = $('#sv3-head-layout');
      if (!lh) {
        lh = el('div', 'sv3-subhead sv3-body-2');
        lh.id = 'sv3-head-layout';
        lh.innerHTML = '<span class="sv3-subhead__title">Choose Your Layout</span>';
      }
      if (tiles.previousElementSibling !== lh) tiles.insertAdjacentElement('beforebegin', lh);
    }

    var fields = photoFields();
    var inputs = $('#teeFormInputs');
    if (!inputs) return;
    var ph = $('#sv3-head-photos', inputs);
    if (!fields.length) { if (ph) ph.remove(); return; }
    if (!ph) {
      ph = el('div', 'sv3-subhead');
      ph.id = 'sv3-head-photos';
      ph.innerHTML =
        '<span class="sv3-subhead__title">Your Photos</span>' +
        '<span class="sv3-subhead__note">' + PHOTO_TIP + '</span>';
    }
    if (fields[0].previousElementSibling !== ph) fields[0].insertAdjacentElement('beforebegin', ph);
  }

  /* ------------------------------------------------------------------ */
  /* Spec ticks                                                          */
  /* ------------------------------------------------------------------ */
  /* Rendered in Liquid now, under the title, by snippets/scottsy-pdp-v3.liquid.
     They were injected from here only because the part of the snippet that
     carried their wording renders after the personaliser; printed from the
     'rating' part instead they land in the right place on their own. */

  /* ------------------------------------------------------------------ */
  /* Trust box — third tick                                              */
  /* ------------------------------------------------------------------ */
  /* Added here rather than in snippets/scottsy-product-trust.liquid, which owns
     the screen-protector cart logic and is deliberately never edited. The row
     is built with the SNIPPET'S own classes, so it inherits exactly the same
     tick styling as the two rows above it and cannot drift from them.

     Wording is Scottsy's own, condensed from the product accordion. It is the
     one thing the buy box does not already say: the rating is at the top of the
     page, delivery is in the card above, and secure checkout is the row's
     heading — UK manufacture appears nowhere until well below the fold.

     TITLE CASE, every word. That is the house style for headings, labels and
     short UI lines across Scottsy, and the two rows above this one already
     follow it ("Error-Free Print Guarantee", "30-Day Satisfaction Guarantee").
     A sentence-case line here is instantly the odd one out. */

  var VAULT_UK = 'Printed &amp; Checked In Our UK Studio';

  function decorateVault() {
    var vault = $('.scottsy-authority-vault');
    if (!vault || $('#sv3-vault-uk', vault)) return;
    var rows = $$('.guarantee-row', vault);
    if (!rows.length) return;
    var row = el('div', 'vault-row guarantee-row',
      '<span class="guarantee-icon">✓</span>' +
      '<span class="guarantee-text">' + VAULT_UK + '</span>');
    row.id = 'sv3-vault-uk';
    rows[rows.length - 1].insertAdjacentElement('afterend', row);
  }

  /* ------------------------------------------------------------------ */
  /* Multibuy                                                            */
  /* ------------------------------------------------------------------ */
  /* Nothing to do here any more. scottsy-product-trust.liquid already builds
     the offer as a solid navy MULTIBUY chip beside a sentence whose amount is
     green — the same markup the live product page uses — and names the range
     correctly from its own CONFIG.offerNoun. This file used to throw that away
     and rebuild it as a single pale-blue pill, which cost a chunk of JS to end
     up with something quieter and bluer than what it replaced.
     The pill is gone. All that is left is placing the snippet's own element in
     the price row, which section 2 of the stylesheet does. */

  /* ------------------------------------------------------------------ */
  /* Price row: delivery estimate                                        */
  /* ------------------------------------------------------------------ */

  function decoratePriceRow() {
    var priceEl = $('.tee-product-price .tee-price--current');
    if (!priceEl) return;

    /* The arrival window carries the emphasis; the styling follows Scottsy's
       own .scottsy-inline-delivery green rail. Dates come from
       snippets/scottsy-delivery-dates.liquid; nothing here depends on the
       dispatch cut-off, which varies. */
    var d = window.ScottsyDelivery;
    if (!d || !d.ready) return;
    var wrap = $('.tee-product-price .tee-price-wrapper') || priceEl.parentNode;
    var box = $('#sv3-delivery');
    if (!box) {
      box = el('div', 'sv3-delivery');
      box.id = 'sv3-delivery';
    }
    /* Estimates only — no dispatch-time or countdown claim anywhere in this
       block. The cut-off varies, so anything phrased as "order within X" would
       be wrong on the days it matters most. */
    /* A parcel, not a tick. The tick is already the page's "this step is
       finished" mark — on the step badges and on the fit line — so repeating it
       here read as a fourth completed thing rather than as delivery. */
    /* Wording comes from Theme settings via #sv3-delivery-copy in
       snippets/scottsy-pdp-v3.liquid, so the seasonal cut-off line can be put
       up and taken down from Customise without a code push. Each field falls
       back to the wording below when the setting is empty, so clearing a box
       in Customise restores the normal card instead of blanking a line. */
    var copyEl = $('#sv3-delivery-copy');
    function dcopy(name, fallback) {
      var v = copyEl && copyEl.getAttribute('data-' + name);
      v = v && v.trim();
      return v ? v : fallback;
    }
    /* The values are author-entered text going into innerHTML, so they are
       escaped here rather than trusted — a stray < in a setting should print,
       not open a tag. */
    function esc(t) {
      return String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
    var note = dcopy('note', '');
    var html =
      '<span class="sv3-delivery__icon" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" fill="currentColor" focusable="false">' +
          '<path d="M12 2.2 2.8 6.8v10.4L12 21.8l9.2-4.6V6.8L12 2.2Zm0 2.3 6.3 3.1' +
          'L12 10.7 5.7 7.6 12 4.5ZM4.8 9.2l6.2 3.1v6.9l-6.2-3.1V9.2Zm8.2 10v-6.9' +
          'l6.2-3.1v6.9l-6.2 3.1Z"/>' +
        '</svg>' +
      '</span>' +
      '<span class="sv3-delivery__text">' +
        '<span class="sv3-delivery__label">' + esc(dcopy('label', 'Free Delivery · Estimated')) + '</span>' +
        '<span class="sv3-delivery__when">' + d.free + '</span>' +
        '<span class="sv3-delivery__express">' +
          esc(dcopy('express-prefix', 'Need it sooner? Express est.')) + ' <b>' +
          d.express + '</b>. ' + esc(dcopy('express-suffix', 'Upgrade available at checkout')) +
        '</span>' +
        (note ? '<span class="sv3-delivery__note">' + esc(note) + '</span>' : '') +
      '</span>';
    /* Compared against the last string WE set, not box.innerHTML: reading
       innerHTML back off a live element re-serializes it, and the browser
       always expands a childless SVG element's self-closing tag (the <path/>
       above) to an explicit pair (<path></path>) on the way out. That makes
       box.innerHTML !== html true on every single call regardless of content,
       permanently defeating a same-value guard written that way — confirmed
       live: a fresh build and the DOM's own read-back differed by exactly the
       "/>":"></path>" swap, nothing else. state.deliveryHtml is a plain JS
       string that never round-trips through the DOM, so it compares clean. */
    if (state.deliveryHtml !== html) {
      box.innerHTML = html;
      state.deliveryHtml = html;
    }
    if (box.parentNode !== wrap) wrap.appendChild(box);
  }

  /* ------------------------------------------------------------------ */
  /* Keyring cross-sell badge                                            */
  /* ------------------------------------------------------------------ */
  /* Same price-row anchor as decoratePriceRow() above, deliberately built
     as its own separate element rather than repurposing the real MULTIBUY
     chip — that chip lives in snippets/scottsy-product-trust.liquid, which
     is never edited (see that file's own header comment / project rule).
     Content comes from #sv3-keyring-bundle-data (scottsy-pdp-v3.liquid),
     only rendered for template_suffix 'keyrings', so this is a no-op on
     every other template without needing its own template_suffix check
     here — same pattern as decorateVault()/injectStandAddon() elsewhere in
     this file. */
  function decorateKeyringBundle() {
    var data = $('#sv3-keyring-bundle-data');
    if (!data) return;
    var priceEl = $('.tee-product-price .tee-price--current');
    if (!priceEl) return;
    var box = $('#sv3-keyring-bundle');
    if (!box) {
      box = el('div', 'sv3-keyring-bundle');
      box.id = 'sv3-keyring-bundle';
    }
    var dealAmount = data.getAttribute('data-amount') || '';
    var dealLabel = data.getAttribute('data-deal-label') || '';
    var note = data.getAttribute('data-note') || '';
    if (!dealAmount) return;
    /* Rebuilt per Scott, v4: a straight price comparison — "on its own" vs
       "with any other item" — rather than a single highlighted offer pill.
       v4's first attempt showed this ALONGSIDE the real price element,
       leaving "£12.99" printed twice on the page ("looks a right mess") —
       fixed by reading priceEl's live text into this box's own copy (so it
       can never drift if the real price ever changes) and then hiding the
       original, rather than duplicating it beside a visible original.

       Hiding rather than MOVING the real node: priceEl is re-queried fresh
       on every refresh() call rather than cached, so if Teeinblue ever
       re-renders its own price element as a new node (it does this
       elsewhere on this page for other fields), this function finds and
       hides whichever one is currently live each time — no risk of an old,
       physically-relocated copy being orphaned behind a freshly-rendered
       replacement. The JSON-LD product schema (snippets/microdata-
       schema.liquid) is generated server-side straight from
       product.variants, entirely independent of this element's visibility
       either way, so hiding it here has no effect on GMC/structured data —
       only on which copy of the same number a shopper sees. */
    var ownAmount = (priceEl.textContent || '').trim();
    if (!ownAmount) return;
    box.innerHTML =
      '<div class="sv3-keyring-bundle__compare">' +
        '<div class="sv3-keyring-bundle__col sv3-keyring-bundle__col--own">' +
          '<span class="sv3-keyring-bundle__col-price">' + ownAmount + '</span>' +
          '<span class="sv3-keyring-bundle__col-label">On Its Own</span>' +
        '</div>' +
        '<div class="sv3-keyring-bundle__divider">or</div>' +
        '<div class="sv3-keyring-bundle__col sv3-keyring-bundle__col--deal">' +
          '<span class="sv3-keyring-bundle__col-price">' + dealAmount + '</span>' +
          (dealLabel ? '<span class="sv3-keyring-bundle__col-label">' + dealLabel + '</span>' : '') +
        '</div>' +
      '</div>' +
      (note ? '<div class="sv3-keyring-bundle__note">' + note + '</div>' : '');
    /* Same insertion point as the real Multibuy chip (injectOfferStrip() in
       scottsy-product-trust.liquid): priceEl.insertAdjacentElement('afterend',
       box), NOT appended to the price wrapper. Scott: "i want it above
       delivery, next to the price - like it is on the other PDPs" — the
       delivery card (decoratePriceRow() above) is appended to the wrapper
       separately and always lands at the end of it regardless of call
       order, so inserting here as price's own next sibling is what actually
       puts this ahead of it, matching the real chip's position exactly. */
    if (box.previousElementSibling !== priceEl) priceEl.insertAdjacentElement('afterend', box);
    priceEl.style.display = 'none';
  }

  /* ------------------------------------------------------------------ */
  /* Enlarged preview — the modal's own chrome                           */
  /* ------------------------------------------------------------------ */
  /* sticky-preview.js owns the lightbox: it fixes .product-gallery centre
     screen, drops a backdrop behind it and a bare x on top, and handles the
     scroll lock and the keyboard. None of that is touched here. What it does
     not have is anything explaining WHAT is on screen or what to do next — it
     is a photo on a dark scrim, and the customer's own case deserves better
     than that at the moment they are deciding whether it looks right.

     So the shell is restyled in CSS and the chrome is injected here: a heading
     naming the model and layout they actually picked, a line about what the
     preview is, and the two things they might want to do next.

     MOBILE ONLY, because the lightbox is. openLightbox() returns early unless
     isMobile() and every rule for it sits inside a max-width: 750px query.
     Nothing here runs on desktop because .tb-lightboxed never appears there. */

  /* The MODEL only. The layout was in here too — "iPhone 13 Mini · 1 Photo" —
     but the layout is a thing the customer is looking straight at in the
     preview, so naming it is just noise. The model is the one fact the picture
     cannot show them and the one most worth double-checking before they buy. */
  function previewSubtitle() {
    /* modelName() only ever reads #product-option-dropdown, which slate's
       Size never is (it's a real radiogroup) — always returns '' there, so
       every slate customer opening the enlarged preview got no subtitle at
       all where a phone/frame customer sees their model name. Same
       distinction updateState() already makes for the step-1 badge. */
    if (sizeRadioGroup()) return sizeName();
    return modelName() || '';
  }

  /* No promise of a human check. An earlier draft of this line offered to spot
     badly-placed faces and contact the customer before printing — Scottsy does
     not do that, and the Q&A and How It Works both say plainly that there is no
     proof stage. A warning that helps costs nothing; a promise that is not kept
     costs a refund and a review. Case-specific: an edge/camera tolerance note
     makes sense on a case, whose print sits around a fixed cutout — a flat
     frame print has no equivalent "this part doesn't move" concern, so rather
     than force a reworded version, mapped non-phone templates get no line at
     all here. Unlike confirm-a/b/step1-heading, an EMPTY override on a mapped
     template means "show nothing", not "fall back to the case default" — so
     this is gated on isPhoneProduct() directly rather than on override being
     truthy, the same way the fit-row/model-help-sheet above are. */
  var PREVIEW_HELP_DEFAULT =
    'Here’s your case. We allow 1–2mm of movement around the edges and camera when printing ' +
    '— give faces and text a little breathing room from the edge.';
  /* Leather's print covers the whole front with no camera cutout — derived
     from the same sentence so a future edit to the base wording can't drift
     the two out of sync. See #sv3-preview-help-flags in the liquid snippet. */
  var PREVIEW_HELP_NO_CAMERA = PREVIEW_HELP_DEFAULT.replace(' and camera', '');
  function previewHelpText() {
    if (isPhoneProduct()) {
      var flags = $('#sv3-preview-help-flags');
      if (flags && flags.getAttribute('data-no-camera-note') === 'true') return PREVIEW_HELP_NO_CAMERA;
      return PREVIEW_HELP_DEFAULT;
    }
    var data = $('#sv3-confirm-copy');
    return data.getAttribute('data-preview-help') || '';
  }

  function desktopPreview() {
    return !!(window.matchMedia && !window.matchMedia('(max-width: 750px)').matches);
  }

  /* ------------------------------------------------------------------ */
  /* Preview watermark — deters screenshotting a customer's own AI/cartoon-   */
  /* transformed photo before they've paid for it. Gated per-product by the  */
  /* watermark tag (snippets/scottsy-pdp-v3.liquid).                        */
  /*                                                                        */
  /* A light, simple repeating pattern (see the CSS) — deliberately not the    */
  /* separate tag+note badge tried before, which Scott didn't like the look    */
  /* of. Safe to bring the tile back now in a way it wasn't on the first two   */
  /* attempts: those targeted multiple .tee-printarea--printarea copies for    */
  /* the one active item (a Teeinblue rendering quirk), and two independently- */
  /* positioned copies of the same tile landed slightly out of phase with      */
  /* each other — visible as ghosting. Host-targeting below is now pinned to   */
  /* the single .tee-mockup-item visibleMockupItem() returns, so there is      */
  /* structurally only ever one tile now, not a pair that can drift apart.     */
  /* Reassurance copy is left to the existing "Design Before You Buy" strip    */
  /* elsewhere on the page rather than duplicated here — simpler is the point. */
  /*                                                                        */
  /* Host: whatever visibleMockupItem() says is active — the same answer     */
  /* mockupPainted() and the enlarged-preview clone already trust — plus a   */
  /* fallback to any sized .tee-mockup-item when that comes up empty.        */
  /* Confirmed live, repeatedly: the stricter check (visible AND sized) can   */
  /* come up empty while the item actually holding content is sitting right   */
  /* there — sized, painted, just also carrying visibility:hidden             */
  /* mid-transition, with every genuinely-empty placeholder around it marked  */
  /* visible instead. That's what "doesn't show until a refresh" actually     */
  /* was: nothing to attach to, every single poll, until a fresh boot()       */
  /* happened to land in a settled state.                                    */
  /*                                                                        */
  /* Also covers the "Adjust Position" dialog when open, detected via         */
  /* aria-expanded="true" on the vue-js-modal root (confirmed live from       */
  /* Scott's own DOM dump) and wrapped in try/catch — researched live:        */
  /* aria-expanded is an accessibility attribute, not that library's real      */
  /* state API, so it's an external guess, not a guarantee. A guess that      */
  /* occasionally misjudges the dialog is a cosmetic gap; a guess that        */
  /* throws and takes the whole function down with it is a much bigger deal.  */
  /*                                                                        */
  /* This is a plain sibling <div> layered on top via CSS, not a canvas and  */
  /* not a modification of Teeinblue's own image element: nothing here ever */
  /* touches the artwork Teeinblue itself tracks, so there is no path from   */
  /* this feature into the file that gets added to cart or sent to print —  */
  /* the watermark exists only as an extra, purely decorative DOM node.      */
  /* ------------------------------------------------------------------ */
  function watermarkEnabled() {
    var flag = $('#sv3-watermark-flag');
    return !!(flag && flag.getAttribute('data-watermark') === 'true');
  }

  /* Teeinblue's dialogs mount their inner content a beat after the outer
     shell appears — confirmed live from Scott's own DOM dump of the image
     cropper: the tight .vue-advanced-cropper__boundaries target isn't in
     the DOM yet on the poll tick right after the dialog opens. Scoping
     "does a badge already exist" to just that tick's chosen host missed
     that — an early tick fell back to a bigger host and planted a badge
     there, then a later tick found the real, tighter host once it mounted
     and planted a second one, with no way to know the first was still
     sitting elsewhere. This checks the whole scope every time (the dialog
     itself for the dialog badge, the gallery for the main preview one) and
     removes anything not parented to *this* tick's host, so a host that
     moves between ticks can never leave a stray one behind. */
  function syncWatermarkBadge(scope, host, placed) {
    if (!scope) return;
    var existing = scope.querySelectorAll('.sv3-preview-watermark');
    if (!placed || !host) {
      for (var i = 0; i < existing.length; i++) existing[i].remove();
      return;
    }
    var kept = false;
    for (var j = 0; j < existing.length; j++) {
      if (existing[j].parentElement === host && !kept) {
        kept = true;
      } else {
        existing[j].remove();
      }
    }
    if (!kept) {
      var badge = el('div', 'sv3-preview-watermark');
      badge.setAttribute('aria-hidden', 'true');
      host.appendChild(badge);
    }
  }

  function syncPreviewWatermark() {
    if (!watermarkEnabled()) return;
    /* photoFieldFilled() — not window.teeinblue.hasCustomization() — because
       it's the check this file already trusts for Add to Cart validation
       (incompleteRequired() above), reading the DOM state a photo upload
       actually produces rather than a Teeinblue API whose true meaning
       turned out not to match "a photo is showing" in practice.

       A tagged product with NO photo field at all (a pre-made clipart
       design, nothing to upload) has no "before vs after personalising"
       moment to gate on — the design is the whole product from first paint,
       so it's watermarked unconditionally rather than never. */
    var fields = photoFields();
    var placed = fields.length === 0 ? true : fields.some(photoFieldFilled);
    var mainHost = visibleMockupItem();
    if (!mainHost) {
      var allItems = $$('.tee-mockup-item');
      for (var mi = 0; mi < allItems.length; mi++) {
        if (allItems[mi].getBoundingClientRect().width > 10) { mainHost = allItems[mi]; break; }
      }
    }
    var dialogHost = null;
    var dialog = null;
    try {
      dialog = document.querySelector('.vm--modal[aria-expanded="true"]');
      /* Teeinblue opens more than one dialog on this same .vm--modal shell,
         each with its own internal markup, so no single class works for all
         of them:
         - .tee-ip-preview-inner — the "Adjust Position" dialog. Confirmed
           from Scott's own DOM dump that this (the actual visible window,
           e.g. 332x332) is smaller than .tee-mockup-item (the full native
           canvas underneath it, e.g. 600x600) sitting inside it. inset:0
           against the bigger box let the pattern paint into space beyond
           what's actually visible.
         - .vue-advanced-cropper__boundaries — the "Image Cropper" dialog
           (tee-dialog-image-cropper), built on the third-party
           vue-advanced-cropper library, confirmed from a second real DOM
           dump. This is the visible photo canvas the crop box sits on top
           of — deliberately not .vue-rectangle-stencil (the draggable
           crop-selection square itself), which is smaller and moves as the
           customer drags it.
         Falls back to .tee-mockup-item for any older/simpler dialog that
         still uses it, then .tee-dcontent-body — the content wrapper both
         known dialogs place ahead of their own .tee-dcontent-footer — so
         even a dialog type this code doesn't specifically know about yet
         still clips above its Cancel/Save buttons rather than sizing to the
         whole modal. Only truly falls back to the dialog root itself if
         none of that Teeinblue-authored structure exists at all.
         None of this is available the instant the dialog opens — Vue mounts
         it a beat later — so which of these actually matches legitimately
         changes from one 600ms poll to the next while a dialog is opening.
         syncWatermarkBadge() below is what keeps that from producing more
         than one badge. */
      if (dialog) dialogHost = dialog.querySelector('.tee-ip-preview-inner') || dialog.querySelector('.vue-advanced-cropper__boundaries') || dialog.querySelector('.tee-mockup-item') || dialog.querySelector('.tee-dcontent-body') || dialog;
      /* inset:0 below sizes against whichever element is actually
         "positioned" (position != static) — not necessarily this one, if
         Teeinblue's own stylesheet never gave it that. Forcing it here is
         harmless to its own layout (position:relative doesn't move an
         element that has no offset values) and guarantees the watermark
         sizes against the box actually intended, not whatever ancestor
         happens to be positioned instead. */
      if (dialogHost && getComputedStyle(dialogHost).position === 'static') {
        dialogHost.style.position = 'relative';
      }
    } catch (e) {
      dialog = null;
      dialogHost = null;
    }
    syncWatermarkBadge(document.querySelector('.product-gallery') || document, mainHost, placed);
    syncWatermarkBadge(dialog, dialogHost, placed);
    /* Same reassurance line the main page's "Design Before You Buy" strip
       already carries. Covers every Teeinblue dialog that carries
       .tee-dcontent-footer/.tee-dcontent-body — confirmed live that
       includes the enlarged preview (.tee-dialog-gallery), not just
       Adjust Position/crop, so decorateTeeinbluePreviewDialog() leaves
       this banner to this function rather than building its own.
       Placed in normal document flow, not overlaid on the
       mockup: everything overlaid on the image itself has been a recurring
       source of misplacement this round, and a plain row of text ahead of
       the dialog's own Cancel/Save footer can't land on top of anything. */
    try {
      if (dialog) {
        var banner = dialog.querySelector('.sv3-preview-watermark-banner');
        if (placed) {
          if (!banner) {
            banner = el('p', 'sv3-preview-watermark-banner', 'Watermark won’t print on your item');
            /* .tee-cropper-footer/.tee-cropper-body — the Image Cropper
               dialog (8f in the CSS) uses its own class names, not
               Teeinblue's more common .tee-dcontent-footer/.tee-dcontent-
               body, confirmed live it shares with every other dialog this
               targets. Without this fallback the banner silently had
               nowhere to land there at all. */
            var dialogFooter = dialog.querySelector('.tee-dcontent-footer') || dialog.querySelector('.tee-cropper-footer');
            var dialogBody = dialog.querySelector('.tee-dcontent-body') || dialog.querySelector('.tee-cropper-body');
            if (dialogFooter && dialogFooter.parentNode) {
              dialogFooter.parentNode.insertBefore(banner, dialogFooter);
            } else if (dialogBody) {
              dialogBody.appendChild(banner);
            }
          }
        } else if (banner) {
          banner.remove();
        }
      }
    } catch (e) {}
  }

  /* Fills the same gap the CSS :has() rules above (search "CLOSING BUG")
     already close on browsers that support it — Samsung Internet < 20,
     Safari iOS < 15.4 and Firefox < 121 don't. On those, .tee-dialog-X
     (Teeinblue's own .vm--container, position:fixed; inset:0) never gets
     neutralised once its dialog closes, so it keeps intercepting every tap
     on the page behind it — including Add to Cart — forever, since Vue
     never actually removes it from the DOM (see the CSS comments on why:
     opacity/transform are pinned to fix the OPEN animation, which as a
     side effect starves transitionend on close too).

     Symmetric, not "block on close and forget": every tick either clears
     pointer-events (this container's own .vm--modal has
     aria-expanded="true" — the exact signal syncPreviewWatermark() above
     already relies on) or applies it (anything else — mid-close, or
     already closed and still sitting in the DOM). That symmetry is what
     makes repeated open → close → reopen → close cycles safe: a reopened
     dialog is detected and unblocked on its own next tick, nothing here
     remembers whether a container was blocked before.

     Its own timer, not syncPreviewWatermark()'s — that one bails out
     immediately for every product without the watermark tag, and this bug
     has nothing to do with watermarking; it has to run on every product
     that can open one of these dialogs.

     Harmless where :has() already works: that CSS rule carries !important
     and wins regardless of what this sets, so this is redundant, inert
     work there — it only actually matters on the browsers :has() can't
     reach. */
  function syncDialogCloseGuard() {
    try {
      /* .tee-dialog-gallery (the enlarged Preview dialog) added — full
         review caught it missing from this list. Without it, on a browser
         that can't evaluate :has() at all, .tee-dialog-gallery's own
         pointer-events:none-while-closing CSS rule (search
         ".tee-dialog-gallery:has(.vm--overlay" in the CSS) never applies,
         so once that dialog is opened and closed once,
         anyTeeinblueDialogReallyOpen() sees it as permanently open —
         freezing the tap-on-artwork reopen path, mobile docking/hiding, and
         the hint bar, same failure mode this guard already exists to
         prevent for the other five dialogs. Same aria-expanded scaffold
         (confirmed elsewhere in this file), so no different handling
         needed. */
      var containers = $$('.tee-dialog-image-placement, .tee-dialog-layer-reposition, .tee-dialog-maps, .tee-dialog-image-cropper, .tee-dialog-route-maps, .tee-dialog-gallery');
      for (var i = 0; i < containers.length; i++) {
        var c = containers[i];
        var open = c.querySelector('.vm--modal[aria-expanded="true"]');
        if (open) {
          if (c.style.pointerEvents === 'none') c.style.pointerEvents = '';
        } else if (c.style.pointerEvents !== 'none') {
          c.style.pointerEvents = 'none';
        }
      }
    } catch (e) {}
  }

  /* :has() fallback (pre-launch audit, M13) — mirrors the CSS
     ":has(.tee-ip-photos)" rules (search "sv3-has-photo-strip" in the CSS)
     with a class instead, so the Adjust Position dialog's toolbar/photo-strip
     overlap fix still applies on Firefox < 121 / Safari iOS < 15.4 / Samsung
     Internet < 20, which can't evaluate :has() at all. Without this, those
     two rules simply never match there, and the "other images show under the
     buttons" bug — already fixed for everyone else — comes back for exactly
     that browser band. Same interval as syncDialogCloseGuard() above: two
     more classList.toggle() calls on the same 600ms tick is cheap, no reason
     for a timer of its own. Harmless where :has() already works — both
     rules set the same value, so it doesn't matter which one the cascade
     actually applies. */
  function syncPhotoStripFallback() {
    try {
      var els = $$('.tee-dialog-image-placement .tee-ip--mockup, .tee-dialog-image-placement .tee-ip-preview--with-mockup');
      for (var i = 0; i < els.length; i++) {
        els[i].classList.toggle('sv3-has-photo-strip', !!els[i].querySelector('.tee-ip-photos'));
      }
    } catch (e) {}
  }

  /* THE PREVIEW IS A STACK, NOT A PICTURE. Teeinblue composes it from layers
     inside .tee-mockup-item: a base mockup image, the print area holding the
     customer's artwork as SVG, and a second image laid over the top. No single
     <img> anywhere contains their design — copying one gave a blank case, which
     is exactly what the first attempt at this shipped.

     So the whole item is cloned and scaled. A clone is a snapshot, which is
     fine because it is rebuilt every time the sheet opens, so it always shows
     what was on screen at that moment. It is also inert: nothing in it is wired
     to Teeinblue, so it cannot be re-rendered out from under us and cannot
     touch the live preview on the page. */
  /* Teeinblue keeps one .tee-mockup-item per model in the DOM and reveals
     the current one by giving it real width/height — every other one stays
     collapsed to 0x0 (same fact mockupPainted() relies on below). The first
     .tee-mockup-item in DOM order is not necessarily the visible one, e.g.
     right after switching models and back — an unscoped first-match grabs
     whichever leftover item happens to sit first, finds a 0-width rect, and
     syncPreviewWatermark() below has nothing real to attach the watermark to.

     width alone is not enough either — confirmed live right after a model
     switch: the outgoing item can sit at visibility:hidden while STILL
     reporting a real getBoundingClientRect().width (hidden, unlike
     display:none, keeps its layout box), so a width-only check can pick a
     hidden leftover over the genuinely showing one depending on DOM order.
     Checking computed visibility as well is what actually distinguishes
     them. */
  function visibleMockupItem() {
    var items = document.querySelectorAll('.product-gallery .tee-mockup-item');
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      if (it.getBoundingClientRect().width > 10 && getComputedStyle(it).visibility !== 'hidden') return it;
    }
    return null;
  }

  /* .tee-dialog alone (what the gallery click-guard below used to check)
     stays true long after a dialog visually closes — confirmed live, a
     "Close preview" tap leaves .tee-dialog-gallery sitting in the DOM,
     inert, for 5+ seconds (same root cause as the CSS "closing bug" fix
     elsewhere in this file: Vue's own leave-transition classes never
     actually change a value, so transitionend never fires and Vue never
     removes the node). A guard keyed on bare presence reads that stale
     node as "a dialog is open" and silently refuses to reopen the
     preview — tapping the artwork again does nothing, for as long as the
     leftover node lingers.

     Checking for the .vm-transition--overlay-leave-active CLASS directly
     (an earlier version of this check did) has its own hole: the overlay
     element itself can be gone — dialog.querySelector('.vm--overlay')
     returns null — while the stale .tee-dialog root it lived in is still
     sitting in the DOM, and "no overlay to check" read as "nothing to
     disqualify it" rather than "this is stale." Reading .tee-dialog's own
     computed pointer-events instead sidesteps that: it's the exact
     property the CSS closing-bug fix already sets to none !important on
     every dialog type while stale/closing (search ":has(.vm--overlay.vm-
     transition--overlay-leave-active)" in the CSS — .tee-dialog-gallery,
     .tee-dialog-image-placement, .tee-dialog-maps, .tee-dialog-image-
     cropper and .tee-dialog-route-maps all carry it), so this reads the
     same live signal the CSS already committed to, instead of
     re-deriving it from class names a second, less reliable way. */
  function anyTeeinblueDialogReallyOpen() {
    var dialogs = document.querySelectorAll('.tee-dialog');
    for (var i = 0; i < dialogs.length; i++) {
      if (getComputedStyle(dialogs[i]).pointerEvents !== 'none') return true;
    }
    return false;
  }

  /* Teeinblue's own "Preview" button (.tee-btn--preview) already opens a
     correctly-sourced, always-in-sync dialog (.tee-dialog-gallery) —
     confirmed live: it holds exactly one freshly-built mockup-item, not a
     reference to anything in the swipeable gallery (tagged all 20 of the
     gallery's own mockup-items and opened this dialog; the dialog's own
     item never carried a tag), and updates correctly and instantly on a
     real variant change, no tricks needed.

     An earlier version of this file borrowed that dialog's content into a
     separate, custom-built shell instead of just showing the dialog
     itself — clone it out, close Teeinblue's own dialog, display the
     clone in our own chrome. That meant fighting Teeinblue's own CSS
     (`.tee-gallery-content ~ * { display: none !important; }`, which
     caught the clone's own host) and sticky-preview.js's own
     dialog-conflict detection (closing an already-open mobile lightbox
     the instant it saw ANY Teeinblue dialog open, including this one,
     confirmed live as a real race) just to reach a picture Teeinblue was
     already showing correctly on its own. This is simpler: let
     Teeinblue's own dialog BE the modal, restyle it with CSS (same
     "rebuilt from the native mechanism, not against it" approach already
     used for the Adjust Position dialog, search "8c." in the CSS), and
     inject the theme's own heading/help text directly into it. */
  /* Fixing the gallery click-guard's own stale-node check (search
     anyTeeinblueDialogReallyOpen()) turned out not to be the whole story:
     confirmed live, .tee-btn--preview's OWN click handler silently does
     nothing if tapped again within roughly a couple of seconds of closing
     the previous one — Teeinblue's internal open/close state trails the
     DOM by that long, independent of anything this file's CSS or JS
     controls or can inspect directly.

     First attempt at working around that clicked the button repeatedly a
     moment apart, hoping one landed once the state settled — confirmed
     live this is actively counter-productive: the button reads as a
     TOGGLE against Teeinblue's own internal state, not "try to open,
     no-op if already open", so clicking it several times in a row can
     cycle it through open/closed an unpredictable number of times and
     land on either. A single stray click is exactly what silently undoes
     a customer's own tap. WAITING for the stale dialog to clear before
     clicking, rather than clicking repeatedly while it's still stale, is
     the version that's actually safe — at most one real click per call,
     ever. */
  function stalePreviewDialogBlocking() {
    var dialog = document.querySelector('.tee-dialog-gallery');
    /* pointer-events, not the .vm--overlay leave-active class directly
       (search anyTeeinblueDialogReallyOpen()'s own comment for why: the
       overlay element itself can already be gone from a stale dialog
       while its .tee-dialog-gallery root lingers, and "no overlay to
       check" is not the same thing as "this one has cleared"). */
    return !!dialog && getComputedStyle(dialog).pointerEvents === 'none';
  }
  function openTeeinbluePreview(waitedMs) {
    waitedMs = waitedMs || 0;
    if (stalePreviewDialogBlocking() && waitedMs < 3000) {
      setTimeout(function () { openTeeinbluePreview(waitedMs + 150); }, 150);
      return;
    }
    var btn = document.querySelector('.tee-btn--preview');
    if (btn) btn.click();
  }

  /* Teeinblue mounts .tee-dialog-gallery's inner content a beat after the
     dialog root itself appears — same timing syncPreviewWatermark() (search
     its own comment) already accounts for on the other Teeinblue dialogs —
     so this is a MutationObserver reacting to whenever that actually
     lands, not a guessed delay. data-sv3-decorated guards against
     double-injecting if Vue re-renders the dialog's contents without
     removing/re-adding the dialog root. */
  function decorateTeeinbluePreviewDialog() {
    var dialog = document.querySelector('.tee-dialog-gallery');
    if (!dialog) return;
    /* .vm--modal is the actual white card — .tee-dialog-gallery itself is
       the outer fixed, full-viewport container (it also holds .vm--overlay,
       the dimmed backdrop). Inserting into the dialog root instead of the
       card landed the heading pinned to the top of the viewport, outside
       the card, on top of the page's own sticky nav — confirmed live.
       Bailing here (without setting data-sv3-decorated) rather than
       falling back to the dialog root lets the observer below retry on
       the next mutation once the card has actually mounted. */
    var modal = dialog.querySelector('.vm--modal');
    if (!modal) return;

    if (!dialog.getAttribute('data-sv3-decorated')) {
      dialog.setAttribute('data-sv3-decorated', '1');

      var head = el('div', 'sv3-tp-head');
      var subText = previewSubtitle();
      head.innerHTML =
        '<span class="sv3-tp-title">Your Preview</span>' +
        (subText ? '<span class="sv3-tp-sub"></span>' : '');
      var sub = head.querySelector('.sv3-tp-sub');
      if (sub) sub.textContent = subText;
      modal.insertBefore(head, modal.firstChild);

      var closeBtn = modal.querySelector('button');
      /* Relabelled from Teeinblue's own default "Close preview" — Scott's
         own preference from the previous custom preview window, carried
         over now that this dialog is the enlarged preview. */
      if (closeBtn) closeBtn.textContent = 'Back to Your Design';
      /* No watermark banner built here — syncPreviewWatermark() (search its
         own comment) already injects one into any open Teeinblue dialog via
         .tee-dcontent-footer/.tee-dcontent-body, and this dialog turned out
         to carry that same markup (confirmed live), so it already lands
         correctly here for free. A second one built in this function
         duplicated it verbatim on screen, and unlike the one in
         syncPreviewWatermark() this one had no way to react to placed()
         toggling — that function's 600ms poll is the one already tracking
         it, so it should stay the single source. */
      var helpText = previewHelpText();
      if (helpText) {
        var help = el('p', 'sv3-tp-help', helpText);
        if (closeBtn && closeBtn.parentNode) closeBtn.parentNode.insertBefore(help, closeBtn);
        else modal.appendChild(help);
      }
    }

    /* Confirmed live, repeatedly, on a fresh dialog open: .vm--modal's
       white background computes correctly (rgb(255,255,255), no image, no
       filter, opacity 1 — checked directly) but PAINTS as if still
       transparent, showing the page underneath through the card — while
       elementsFromPoint at the same pixel correctly names the modal as
       topmost the whole time. Toggling any inline style on the modal
       (even setting then immediately removing one) forces a correct
       repaint with no other change, which is what confirmed this is a
       stale compositor paint rather than a real cascade/specificity bug —
       landed inserting new children (above) into .vm--modal right as
       Vue's own enter-transition classes are settling off it.
       How long it takes to clear on its own (with no nudge at all) varies
       a lot even across back-to-back opens in the same session — seen it
       resolve anywhere from under a second to several seconds — which
       reads as this riding on some other unrelated poll's incidental DOM
       writes rather than a fixed-length transition. A couple of nudges at
       guessed delays chased that inconsistently. Repeating on a short
       interval for a few seconds instead needs no correct guess — it's a
       single style write + layout read + style clear each tick, cheap
       enough to just cover the whole observed window. requestAnimationFrame
       would be the wrong primitive for this even so: browsers can suspend
       rAF entirely while a tab is occluded/hidden, and a nudge that never
       fires leaves the dialog stuck exactly as broken as without one.

       Re-arms on every genuine open, not just the dialog's first-ever one —
       full review caught that this whole nudge used to live behind
       data-sv3-decorated above, which is correctly permanent for the
       one-time head/help-text injection but wrong for this: the dialog root
       is a reused node (Vue never removes it, per the closing-bug notes
       elsewhere in this file), and this function's OWN comment above already
       says the paint bug's timing "varies a lot even across back-to-back
       opens in the same session" — i.e. by its own account it can recur,
       so gating the fix behind a permanent flag left every open after the
       first unprotected. data-sv3-tp-was-open tracks open/closed transitions
       (via the same getComputedStyle(dialog).pointerEvents !== 'none' signal
       anyTeeinblueDialogReallyOpen() uses elsewhere) so the nudge re-fires on
       each fresh open without re-running on every unrelated mutation while
       already open. */
    var reallyOpen = getComputedStyle(dialog).pointerEvents !== 'none';
    var wasOpen = dialog.getAttribute('data-sv3-tp-was-open') === '1';
    if (reallyOpen && !wasOpen) {
      dialog.setAttribute('data-sv3-tp-was-open', '1');
      var nudgeTicks = 0;
      var nudgeTimer = setInterval(function () {
        modal.style.opacity = '0.999';
        void modal.offsetHeight;
        modal.style.opacity = '';
        nudgeTicks++;
        if (nudgeTicks >= 15) clearInterval(nudgeTimer);
      }, 200);
    } else if (!reallyOpen && wasOpen) {
      dialog.removeAttribute('data-sv3-tp-was-open');
    }
  }

  /* finding #11 (full review) — without a dummy history entry, Android's
     back gesture/button navigates off the product page instead of just
     closing this dialog. Same pattern already used elsewhere in this file
     (the help sheet) for the same reason — pushed the instant the dialog
     is seen open, consumed (history.back()) on whichever close path fires
     first so a real back-navigation never double-fires this one. */
  var teeinbluePreviewHistoryPushed = false;

  /* Bound once, idempotent via data-sv3-tp-observed. Everything the
     enlarged preview needs converges on openTeeinbluePreview() here: the
     docked thumbnail's own expand icon (.tb-expand, sticky-preview.js)
     otherwise opens the swipeable gallery blown up fullscreen instead —
     redirected the same way bindPreviewButton() used to redirect
     .tee-btn--preview in the old version of this mechanism, capture phase
     so it runs before sticky-preview.js's own handler and can stop it
     cleanly — and tapping the artwork itself, which everyone tries first
     regardless of whether an expand icon is even visible. */
  function bindTeeinbluePreviewDialog() {
    /* document.documentElement, not #teeinblue-wrapper or document.body —
       confirmed live (on the "MUMMY Letter Tile" product) that
       #teeinblue-wrapper doesn't even contain #tee-artwork-form, let alone
       whatever container Vue-js-modal portals its dialogs into; AND that
       document.body itself gets replaced wholesale at some point after this
       runs, orphaning an observer already attached to the old reference
       (confirmed live via a diagnostic build — same finding, same fix, as
       sticky-preview.js's enforce16px() boot()). <html> is never swapped the
       same way. Doubles as the idempotency-flag host below, which
       documentElement supports exactly as well as body did. */
    var root = document.documentElement;
    if (root.getAttribute('data-sv3-tp-observed')) return;
    root.setAttribute('data-sv3-tp-observed', '1');

    new MutationObserver(function () {
      var open = !!document.querySelector('.tee-dialog-gallery');
      if (open) {
        decorateTeeinbluePreviewDialog();
        if (!teeinbluePreviewHistoryPushed) {
          try {
            history.pushState({ sv3TeePreview: true }, '', location.href);
            teeinbluePreviewHistoryPushed = true;
          } catch (e) {}
        }
      } else {
        teeinbluePreviewHistoryPushed = false;
      }
    }).observe(root, { childList: true, subtree: true });

    window.addEventListener('popstate', function () {
      if (!teeinbluePreviewHistoryPushed) return;
      teeinbluePreviewHistoryPushed = false;
      var dialog = document.querySelector('.tee-dialog-gallery');
      var closeBtn = dialog ? dialog.querySelector('button') : null;
      if (closeBtn) closeBtn.click();
    });

    document.addEventListener('click', function (e) {
      var pill = e.target.closest && e.target.closest('.tb-expand');
      if (!pill) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      openTeeinbluePreview();
    }, true);

    /* Delegated on document, like the .tb-expand listener above — not bound
       directly to a captured .product-gallery node. Full review caught that
       the direct-bind version had no self-heal path: this whole function
       only runs once per page load (the data-sv3-tp-observed guard above),
       so a captured node has no later chance to be re-queried the way most
       "bind once" helpers in this file do (each re-checks a per-node
       data-sv3-*-bound attribute on every refresh() pass). sticky-preview.js
       already treats .product-gallery replacement as a real, defended-
       against risk (its own nodes() helper, called before every operation
       that touches gallery/content) — delegation sidesteps needing the same
       defence here, since a click is matched against whatever the live DOM
       looks like at the moment it happens, not a stale reference. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest('.product-gallery')) return;
      if (anyTeeinblueDialogReallyOpen()) return;
      if (e.target.closest('button, a, input, select, [role="button"]')) return;
      openTeeinbluePreview();
    });
  }

  /* Adjust Position, Text Reposition, Map Location, Route Map and Image
     Cropper all visibly resize after they first appear — confirmed live,
     twice over, on Adjust Position specifically: .vm--modal opens at
     892px tall then settles to 649px about a second later, and that
     swing has two separate, independently confirmed causes stacked on
     top of each other. One is genuinely native to Teeinblue and happens
     even on the untouched live theme (198078464374) — their own canvas
     renders at a default ~600px assumption before their JS measures the
     real container and resizes it to fit, confirmed by tracing the exact
     same upload there and seeing the same class of jump (809px→889px,
     smaller, but the same native settle). The other is this file's own:
     the toolbar position and the .tee-ip-preview--with-mockup margin
     reservation are keyed off :has(.tee-ip-photos) (search "THE ACTUAL
     OVERLAPS" in the CSS), and Teeinblue mounts that photo-switcher strip
     a beat after the dialog root, so for that first beat the reservation
     is sized for "no strip" and then jumps once it exists.

     Rather than chase whichever of those (or a third, unfound one) is
     responsible on any given open, this hides the dialog the instant it
     appears and only reveals it once its OWN height genuinely stops
     changing — a direct measurement of the actual symptom, correct
     regardless of which underlying cause produced it, and regardless of
     whether Teeinblue adds a new one later.

     opacity + pointer-events, not visibility: confirmed live elsewhere in
     this file (search .sv3-preview-watermark's own comment) that
     visibility:hidden on an ancestor can still be overridden back to
     visible by a plain (no !important needed) visibility:visible on a
     descendant — exactly what that watermark rule itself relies on to
     survive a DIFFERENT native visibility:hidden this app applies
     elsewhere. Hiding this dialog with visibility would risk exactly that
     leaking through on whichever descendant this file or Teeinblue's own
     stylesheet ever sets visibility on. Opacity has no such escape hatch
     — a parent's opacity:0 flattens everything under it regardless of
     what any descendant's own opacity claims — so pairing it with
     pointer-events:none (opacity alone does not block interaction) is the
     combination that is actually safe to layer over content neither this
     file nor Teeinblue's own CSS fully controls.

     getBoundingClientRect() stays real either way (opacity is paint-only,
     never layout), so nothing here can feed Teeinblue's own sizing math
     a wrong measurement while it's hidden — confirmed live it settles to
     the same final height whether or not this is watching. */
  function bindDialogSettleReveal() {
    // document.documentElement, not #teeinblue-wrapper or document.body — see
    // bindTeeinbluePreviewDialog()'s note above.
    var root = document.documentElement;
    if (root.getAttribute('data-sv3-settle-observed')) return;
    root.setAttribute('data-sv3-settle-observed', '1');

    var SEL = '.tee-dialog-image-placement, .tee-dialog-layer-reposition, .tee-dialog-maps, .tee-dialog-route-maps, .tee-dialog-image-cropper';
    var STABLE_TICKS_NEEDED = 3;
    var POLL_MS = 80;
    var MAX_WAIT_MS = 3000;

    function settleThenReveal(dialog) {
      var modal = dialog.querySelector('.vm--modal');
      if (!modal) { dialog.removeAttribute('data-sv3-settling'); return; }
      dialog.classList.add('sv3-dialog-settling');
      var lastHeight = null;
      var stableCount = 0;
      /* Date.now() against a start timestamp, not elapsed += POLL_MS —
         confirmed live this matters, not just theoretical: setInterval
         ticks measured 900ms+ apart instead of the requested 80ms under
         load in testing, and a per-tick counter has no way to know that
         happened — it just assumes every tick was exactly POLL_MS after
         the last, silently under-counting real elapsed time by however
         much each tick was actually delayed. A busy main thread right
         after a large upload (decoding the image, Teeinblue's own JS)
         is exactly the situation this cap exists to protect against, so
         it has to stay correct under that exact condition, not only when
         nothing else is competing for the thread. */
      var startedAt = Date.now();
      var timer = setInterval(function () {
        if (!dialog.isConnected) { clearInterval(timer); dialog.removeAttribute('data-sv3-settling'); return; }
        var h = modal.getBoundingClientRect().height;
        if (h === lastHeight && h > 0) stableCount++;
        else stableCount = 0;
        lastHeight = h;
        if (stableCount >= STABLE_TICKS_NEEDED || (Date.now() - startedAt) >= MAX_WAIT_MS) {
          clearInterval(timer);
          dialog.classList.remove('sv3-dialog-settling');
          dialog.setAttribute('data-sv3-settled', '1');
          dialog.removeAttribute('data-sv3-settling');
        }
      }, POLL_MS);
    }

    /* Full review caught that data-sv3-settled, once set, was never cleared —
       these 5 dialog roots are reused nodes (Vue never removes them, per the
       closing-bug notes elsewhere in this file), so the settle-then-reveal
       treatment only ever ran on each dialog TYPE's first-ever open in a
       session; reopening the same dialog (an entirely ordinary "adjust it
       again" flow) skipped straight past the guard above and showed the raw
       native resize jump this function exists to hide. Clearing the flag the
       moment a dialog goes back to genuinely closed — the same
       pointerEvents !== 'none' signal anyTeeinblueDialogReallyOpen() already
       uses elsewhere in this file — re-arms it for the next open. */
    new MutationObserver(function () {
      var dialogs = document.querySelectorAll(SEL);
      for (var i = 0; i < dialogs.length; i++) {
        var d = dialogs[i];
        if (getComputedStyle(d).pointerEvents === 'none') {
          if (d.getAttribute('data-sv3-settled')) d.removeAttribute('data-sv3-settled');
          continue;
        }
        if (d.getAttribute('data-sv3-settling') || d.getAttribute('data-sv3-settled')) continue;
        d.setAttribute('data-sv3-settling', '1');
        settleThenReveal(d);
      }
    }).observe(root, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------ */
  /* "Create My Case" — below-the-fold buttons back to step 1            */
  /* ------------------------------------------------------------------ */

  function bindCreateCta() {
    $$('[data-sv3-cta]').forEach(function (btn) {
      if (btn.getAttribute('data-sv3-bound')) return;
      btn.setAttribute('data-sv3-bound', '1');
      btn.addEventListener('click', function () {
        ensureStepOpen(1);
        scrollToEl($('#pdp-form') || $('#sv3-step1') || $('#tee-artwork-form'));
        /* focus lands on the step 1 header — a real control — rather than the
           anchor div, and waits for the smooth scroll so the browser does not
           jump the page a second time to bring focus into view */
        setTimeout(function () {
          var head = $('#sv3-step1');
          if (!head || !head.focus) return;
          /* iOS <= 14 ignores the preventScroll option outright and jumps
             to bring the focused element into view a second time, on top
             of the smooth scroll just above (pre-launch audit, cosmetic).
             Save/restore scroll position around the call so those browsers
             end up matching the ones that honour the option — a no-op
             anywhere preventScroll already works, since scrollY won't
             have moved. */
          var beforeY = window.scrollY;
          head.focus({ preventScroll: true });
          if (window.scrollY !== beforeY) window.scrollTo(window.scrollX, beforeY);
        }, 600);
      });
    });
  }

  /* No sticky "Create My Case". It was tried as a fixed pill that appeared
     once the form scrolled out of view, and removed at Scottsy's request —
     the closing band's button already puts the CTA where a reader has just
     finished reading, and a permanently pinned copy earned nothing for the
     space it took. (The reviews strip carried a second one until that section
     was removed for quoting reviews nobody wrote.) */

  /* ------------------------------------------------------------------ */
  /* "Design before you buy" strip under the gallery                     */
  /* ------------------------------------------------------------------ */

  function placeTryStrip() {
    var gallery = $('.product-gallery');
    if (!gallery || !gallery.parentNode) return;
    var strip = $('#sv3-try');
    if (!strip) {
      /* The watermark sentence only where watermarkEnabled() is true — this
         used to print unconditionally, so a product with no watermark
         (untagged, or a pre-made design with nothing to protect) reassured
         customers about something they would never actually see
         (pre-launch audit, M11). watermarkEnabled() reads a static,
         Liquid-rendered flag, not something that changes mid-session, so
         deciding this once at build time (rather than re-checking on every
         refresh()) is enough. */
      strip = el('div', 'sv3-try',
        '<b class="sv3-try__head">Design Before You Buy</b>' +
        '<span class="sv3-try__body">Your live preview appears here as you personalise.' +
        (watermarkEnabled() ? ' Watermarks are not printed on your final item.' : '') +
        '</span>');
      strip.id = 'sv3-try';
    }
    /* A SIBLING of the gallery, not a child of it. Inside .product-gallery the
       strip was dragged along into sticky-preview.js's docked and fullscreen
       states, which made the sticky preview behave oddly, and it was also
       caught by the gallery's inline `.tee-gallery-content ~ *` hide rule.
       Outside it, sticky-preview never touches it. .product is a two-column
       grid, so the CSS pins the strip to column 1 row 2 and spans .product-info
       across both rows — otherwise a third child auto-places into the buy-box
       column and pushes the buy box down an entire row. */
    if (gallery.nextElementSibling !== strip) gallery.insertAdjacentElement('afterend', strip);
  }

  /* ------------------------------------------------------------------ */
  /* Model help bottom sheet                                             */
  /* ------------------------------------------------------------------ */

  /* photo-frame renders both the frame-comparison and GPX-help variants
     (Liquid can't tell which one a given campaign needs — see the note in
     scottsy-pdp-v3.liquid), so opening the sheet needs to say which one to
     show. Phone-case templates only ever render the one 'phone' variant,
     where this is a no-op either way. */
  var SHEET_TITLES = { phone: 'Find Your Phone Model', frame: 'Compare Our Frames', gpx: 'How To Get A GPX File', sizeHelp: 'Which Size Is Right For Me?' };
  /* Shared by toggleSheet()/trapSheetTab()/the popstate handler below —
     full review, findings #4 and #11. */
  var sheetLastFocus = null;
  var sheetHistoryPushed = false;

  /* Real, currently-visible focusable elements inside the open sheet, in
     DOM/tab order — the basis for both moving focus in on open and
     cycling it on Tab (finding #4). offsetParent is null for anything
     display:none, which covers the sibling .sv3-sheet-variant blocks the
     open/close logic above already hides. */
  function sheetFocusables(backdrop) {
    var sheet = $('.sv3-sheet', backdrop) || backdrop;
    return $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', sheet)
      .filter(function (el) { return el.offsetParent !== null; });
  }

  /* Keeps Tab from ever reaching page content behind the backdrop — the
     hand-rolled sheet has role="dialog" aria-modal="true" (Liquid) but,
     unlike the theme's own FocusTrap-backed dialog-element, nothing was
     actually enforcing that promise (finding #4). Bound once in bindSheet()
     below; checks is-open itself so it's a no-op whenever the sheet isn't
     the thing on screen. */
  function trapSheetTab(e) {
    if (e.key !== 'Tab') return;
    var backdrop = $('#sv3-sheet-backdrop');
    if (!backdrop || !backdrop.classList.contains('is-open')) return;
    var focusables = sheetFocusables(backdrop);
    if (!focusables.length) { e.preventDefault(); return; }
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function toggleSheet(open, variant) {
    var backdrop = $('#sv3-sheet-backdrop');
    if (!backdrop) return;
    if (open && variant) {
      $$('.sv3-sheet-variant', backdrop).forEach(function (v) {
        v.hidden = v.getAttribute('data-sv3-sheet-variant') !== variant;
      });
      var title = $('#sv3-sheet-title', backdrop);
      if (title && SHEET_TITLES[variant]) title.textContent = SHEET_TITLES[variant];
    }
    var wasOpen = backdrop.classList.contains('is-open');
    backdrop.classList.toggle('is-open', !!open);
    document.body.classList.toggle('sv3-sheet-lock', !!open);

    if (open && !wasOpen) {
      /* finding #4 — move focus in, and remember where it came from so
         closing can put it back rather than stranding it on a button
         that's about to have aria-modal content pop up over it. */
      sheetLastFocus = document.activeElement;
      var focusables = sheetFocusables(backdrop);
      if (focusables.length) focusables[0].focus();
      /* finding #11 — a dummy history entry so the next back
         gesture/button pops it instead of leaving the page. Real
         navigation never happens (the URL doesn't change), so this is
         inert except for giving back-navigation something to consume
         first. */
      try {
        history.pushState({ sv3Sheet: true }, '', location.href);
        sheetHistoryPushed = true;
      } catch (err) {}
    } else if (!open && wasOpen) {
      if (sheetLastFocus && document.body.contains(sheetLastFocus)) {
        try { sheetLastFocus.focus(); } catch (err) {}
      }
      sheetLastFocus = null;
      /* Only consume our own history entry via a real back-navigation when
         THIS call is what's closing the sheet (X/OK/backdrop/Escape) — the
         popstate handler below already resets the flag to false before
         calling this when the close was itself caused by the user
         going back, so this branch correctly does nothing in that case
         instead of firing a second, unwanted back-navigation. */
      if (sheetHistoryPushed) {
        sheetHistoryPushed = false;
        try { history.back(); } catch (err) {}
      }
    }
  }

  function bindSheet() {
    var backdrop = $('#sv3-sheet-backdrop');
    if (!backdrop) return;

    /* .product-info is position:sticky, which creates a stacking context — a
       sheet rendered inside it can never paint above the sticky gallery no
       matter how high its z-index. Reparenting to <body> is the only fix. */
    if (backdrop.parentNode !== document.body) document.body.appendChild(backdrop);

    if (backdrop.getAttribute('data-sv3-bound')) return;
    backdrop.setAttribute('data-sv3-bound', '1');
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) toggleSheet(false);
    });
    $$('.sv3-sheet-close, .sv3-sheet-ok', backdrop).forEach(function (btn) {
      btn.addEventListener('click', function () { toggleSheet(false); });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') toggleSheet(false);
    });
    document.addEventListener('keydown', trapSheetTab);
    /* finding #11 — the actual back-gesture/button handler. See the
       history.pushState()/history.back() pair inside toggleSheet() above
       for the full mechanism. */
    window.addEventListener('popstate', function () {
      var bd = $('#sv3-sheet-backdrop');
      if (bd && bd.classList.contains('is-open')) {
        sheetHistoryPushed = false;
        toggleSheet(false);
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Map location dialog — title cleanup only, everything else is CSS   */
  /* ------------------------------------------------------------------ */

  /* Teeinblue's own dialog title carries a trailing "::" ("Map 1
     Location::") — their own template quirk, not something this file
     writes. Same trailing-colon trim as slotName() uses for photo-field
     headings. Returns true once the title has been found (and is clean),
     so the poll below can stop as soon as Teeinblue has rendered it. */
  function cleanMapsDialogTitle() {
    var span = $('.tee-dialog-maps .tee-dcontent-title span');
    if (!span) return false;
    var clean = (span.textContent || '').replace(/[:\s]+$/, '');
    if (span.textContent !== clean) span.textContent = clean;
    return true;
  }

  /* Not per-template like the frame copy elsewhere in this file — this
     dialog's mechanism (search, zoom, drag the pin) is the same wherever
     .tee-field-maps appears, so one hardcoded line is enough, the same
     idiom as PHOTO_TIP near the top of this file. */
  var MAPS_HELP_TEXT = 'Search for your location below, zoom in for precision, then press and hold the pin to drag it exactly into place.';

  function injectMapsHelp() {
    var header = $('.tee-dialog-maps .tee-dcontent-header-wrapper');
    if (!header) return false;
    if ($('.sv3-maps-help', header)) return true;
    var title = $('.tee-dcontent-title', header);
    var p = el('p', 'sv3-maps-help', MAPS_HELP_TEXT);
    if (title) title.insertAdjacentElement('afterend', p); else header.appendChild(p);
    return true;
  }

  /* Teeinblue's own primary-button label — "Select & continue" reads like
     mid-flow copy for a dialog that's really just confirming where the pin
     sits. Scott: "lets change select and continue into Save." */
  function renameMapsSaveButton() {
    var span = $('.tee-dialog-maps .tee-dcontent-footer .tee-btn--primary span');
    if (!span) return false;
    if (span.textContent !== 'Save') span.textContent = 'Save';
    return true;
  }

  /* This dialog is Teeinblue's own vue-js-modal, portalled to <body> like
     the others — outside #tee-artwork-form, so the form's own
     MutationObserver-driven refresh() never sees it render. Polled rather
     than a single fixed delay, so this stays correct regardless of exactly
     how long Teeinblue takes to mount the dialog's content after the click. */
  /* Same "Select & continue" → "Save" rename as the location dialog,
     applied to the GPX route dialog's own primary button — same wording
     issue, same fix, kept as a separate function since the two dialogs'
     footers aren't guaranteed to both be in the DOM at once and a wrong
     match would rename the wrong dialog's button. */
  function renameRouteMapsSaveButton() {
    var span = $('.tee-dialog-route-maps .tee-dcontent-footer .tee-btn--primary span');
    if (!span) return false;
    if (span.textContent !== 'Save') span.textContent = 'Save';
    return true;
  }

  /* Cropper dialog — "Save" (not "Select") on its primary button. No help
     text and no title here (removed deliberately, not an oversight): Scott,
     "check my live theme... if the helper text makes it difficult, remove
     it, and the header if that is what is needed" — confirmed live on the
     true unmodified theme that Teeinblue's own dialog has neither, and that
     vue-advanced-cropper's own canvas needs every pixel it can get. */
  function renameCropperSaveButton() {
    var span = $('.tee-dialog-image-cropper .tee-cropper-footer .tee-btn--primary span');
    if (!span) return false;
    if (span.textContent !== 'Save') span.textContent = 'Save';
    return true;
  }

  /* Name-tile / crossword campaigns ("First Tile Names", "Your Names", etc.)
     use Teeinblue's own .tee-scrabble-wrapper: type a word, press space, it
     locks in as its own removable tile and the box clears for the next word
     — confirmed live by setting the input's value through the native setter
     with a trailing space and dispatching a real input event. Nothing in
     that chip UI explains the space step, so every field of this type gets
     the same line. Not gated to phone/frame products like buildLayoutTiles
     — this can appear on any product with this field, keyed off the DOM
     only. Idempotent per field, same as the dialog help injectors above. */
  var SCRABBLE_HELP_TEXT = 'Type a name, then press space — it locks in as its own tile so you can start the next.';
  function injectScrabbleHelp() {
    $$('.tee-scrabble-wrapper').forEach(function (wrapper) {
      var field = wrapper.closest('.tee-field');
      if (!field) return;
      if ($('.sv3-scrabble-help', field)) return;
      var input = wrapper.closest('.tee-field__input') || wrapper;
      var p = el('p', 'sv3-scrabble-help', SCRABBLE_HELP_TEXT);
      input.insertAdjacentElement('afterend', p);
    });
  }
  function bindCropperPhotoUploads() {
    $$('input[type=file]').forEach(function (input) {
      if (input.getAttribute('data-sv3-cropper-bound')) return;
      input.setAttribute('data-sv3-cropper-bound', '1');
      input.addEventListener('change', function () {
        var tries = 0;
        var poll = setInterval(function () {
          tries++;
          var btnDone = renameCropperSaveButton();
          if (btnDone || tries >= 20) clearInterval(poll);
        }, 150);
      });
    });
  }

  /* "How To Get A GPX File" — opens the shared bottom sheet's 'gpx' variant
     (steps verified against Strava's own help centre, see the note in
     scottsy-pdp-v3.liquid). Placed directly under the route_maps field
     itself, same placeAfter() idiom used for the model-help row. */
  function placeGpxHelpBtn() {
    var field = $('.tee-field--route_maps');
    if (!field) return;
    /* .tee-field--route_maps bundles three unrelated controls as siblings —
       .tee-field__input (the actual upload dropzone, .tee-gpx inside it)
       then two .tee-field__subfield blocks (elevation multiplier, map
       style) that have nothing to do with getting hold of a GPX file.
       Anchoring on the whole field (confirmed live) put this button below
       all three; anchoring on .tee-field__input specifically puts it
       directly under the upload control itself, before the unrelated ones. */
    var uploadWrap = $('.tee-field__input', field) || field;
    var btn = $('#sv3-gpx-help-btn');
    if (!btn) {
      btn = el('button', 'sv3-gpx-help-btn', 'How To Get A GPX File');
      btn.type = 'button';
      btn.id = 'sv3-gpx-help-btn';
      btn.addEventListener('click', function () { toggleSheet(true, 'gpx'); });
    }
    placeAfter(btn, uploadWrap);
  }

  function bindMapsFieldClicks() {
    $$('.tee-field-maps').forEach(function (field) {
      if (field.getAttribute('data-sv3-bound')) return;
      field.setAttribute('data-sv3-bound', '1');
      field.addEventListener('click', function () {
        var tries = 0;
        var poll = setInterval(function () {
          tries++;
          var titleDone = cleanMapsDialogTitle();
          var helpDone = injectMapsHelp();
          var btnDone = renameMapsSaveButton();
          if ((titleDone && helpDone && btnDone) || tries >= 10) clearInterval(poll);
        }, 100);
      });
    });
    /* The GPX route dialog opens after a file upload finishes processing,
       not on a direct field click — bound on the file input's own change
       event instead, same idea as the location field's click binding. */
    $$('.tee-field--route_maps input[type=file]').forEach(function (input) {
      if (input.getAttribute('data-sv3-bound')) return;
      input.setAttribute('data-sv3-bound', '1');
      input.addEventListener('change', function () {
        var tries = 0;
        var poll = setInterval(function () {
          tries++;
          if (renameRouteMapsSaveButton() || tries >= 20) clearInterval(poll);
        }, 150);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Legacy price/progress bar — markup no longer rendered                */
  /* ------------------------------------------------------------------ */
  /* #sv3-bar was the old mobile price bar; its markup is gone from the snippet
     and both of these return early without it. Kept so restoring the markup is
     all it would take. */

  function bindBar() {
    var bar = $('#sv3-bar');
    if (!bar || bar.getAttribute('data-sv3-bound')) return;
    bar.setAttribute('data-sv3-bound', '1');
    document.body.classList.add('sv3-bar-on');

    $('.sv3-bar-btn', bar).addEventListener('click', function () {
      var status = computeStatus();
      if (!status.model) { ensureStepOpen(1); scrollToEl($('#sv3-step1')); return; }
      if (!status.layout || !status.photosDone) { ensureStepOpen(2); scrollToEl($('#sv3-step2')); return; }
      /* the bar is not currently rendered, but if it is ever restored it must
         respect the same two-box gate as the real button */
      if (!confirmsMet()) { flagIncomplete(); return; }
      var atc = $('#teeAtcButton');
      if (atc) atc.click();
    });

    /* hide while typing (mirrors sticky-preview.js keyboard handling) */
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || (t.tagName !== 'INPUT' && t.tagName !== 'TEXTAREA')) return;
      if (t.closest('#tee-artwork-form, .tee-customization-wrapper')) {
        document.body.classList.add('sv3-bar-hidden');
      }
    });
    document.addEventListener('focusout', function () {
      setTimeout(function () {
        var a = document.activeElement;
        if (!a || (a.tagName !== 'INPUT' && a.tagName !== 'TEXTAREA')) {
          document.body.classList.remove('sv3-bar-hidden');
        }
      }, 150);
    });

    document.addEventListener('teeinblue-event-after-cart-added', function () {
      state.added = true;
      if (state.addedTimer) clearTimeout(state.addedTimer);
      state.addedTimer = setTimeout(function () { state.added = false; updateState(); }, 2500);
      updateState();
    });
  }

  /* .product-gallery (the live preview) is position:sticky; top:0 on EVERY
     breakpoint (sticky-preview.css) — but only OVERLAYS anything on mobile.
     Desktop keeps it as a side column next to the form (sticky so it doesn't
     scroll away, never on top of anything), so measuring it there would add
     needless clearance below a target that was never actually covered —
     confirmed live: the column runs 572px tall at a common desktop size,
     which would have pushed every flagged field that much further down for
     no reason. Mobile is the real, and only, case that needs this: once the
     preview has docked (sticky-preview.js's DOCK_AT), it becomes a top bar
     that DOES sit over whatever's beneath it, and scrollToEl's flat -90px
     clearance (tuned for desktop) isn't enough to clear it — confirmed live,
     Scott: "the live preview is still docked... it's hidden behind that."
     desktopPreview() is the same mobile/desktop check already used
     elsewhere in this file. Even on mobile, only returns non-zero when the
     gallery is ACTUALLY pinned right now (rect.top near 0) — mid-page this
     is a no-op and the flat 90px stands. */
  function stickyHeaderClearance() {
    if (desktopPreview()) return 0;
    var gallery = document.querySelector('.product-gallery');
    if (!gallery) return 0;
    var r = gallery.getBoundingClientRect();
    if (r.top > 4) return 0;
    return r.bottom;
  }

  function scrollToEl(target) {
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.pageYOffset - Math.max(90, stickyHeaderClearance() + 16);
    var from = window.pageYOffset;
    try {
      window.scrollTo({ top: top, behavior: 'smooth' });
    } catch (e) {
      window.scrollTo(0, top);
      return;
    }
    /* Smooth scrolling is not always honoured — some browsers, and any device
       with animations turned off at OS level, ignore `behavior: smooth` and
       leave the page exactly where it was. A "Create My Case" button that
       silently does nothing is far worse than one that jumps, so if nothing has
       moved a moment later, go there directly. */
    setTimeout(function () {
      if (Math.abs(window.pageYOffset - from) < 2 && Math.abs(from - top) > 4) {
        window.scrollTo(0, top);
      }
    }, 120);
  }

  /* The WhatsApp app embed pins a 48px bubble at bottom:16px/right:16px. It
     lives in an open shadow root, so the only way to move it is a stylesheet
     injected into that root. The embed loads after cookie consent, hence the
     bounded retry. */
  function liftChatWidget(tries) {
    var host = document.querySelector('gty-whatsapp-chat-button');
    var root = host && host.shadowRoot;
    if (!root) {
      if (tries < 20) setTimeout(function () { liftChatWidget(tries + 1); }, 700);
      return;
    }
    if (root.querySelector('#sv3-chat-offset')) return;
    var style = document.createElement('style');
    style.id = 'sv3-chat-offset';
    style.textContent =
      '@media (max-width: 999px) { .gty-wacb-wrapper { bottom: 104px !important; } }';
    root.appendChild(style);
  }

  /* ------------------------------------------------------------------ */
  /* Progressive state                                                   */
  /* ------------------------------------------------------------------ */

  function computeStatus() {
    var fields = photoFields();
    var filled = fields.filter(photoFieldFilled).length;
    var layout = layoutChosen();
    var photosDone = layout && fields.length > 0 && filled >= fields.length;
    return {
      model: modelChosen(),
      layout: layout,
      photoTotal: fields.length,
      photoFilled: filled,
      photosDone: photosDone,
      ready: modelChosen() && photosDone
    };
  }

  function setSummary(n, text) {
    var s = $('[data-sv3-summary="' + n + '"]');
    if (s && s.textContent !== text) s.textContent = text;
  }

  function updateState() {
    var s = computeStatus();

    /* Numbers are read back off the step headers' own data-sv3-step-key —
       see tagStepMembers() — so this can never disagree with which step
       actually got which number for this product. A null here just means
       that step doesn't exist on this product; setBadge/setSummary are
       skipped for it rather than writing to nothing. */
    function numFor(key) {
      var h = $('[data-sv3-step-key="' + key + '"]');
      return h ? parseInt(h.getAttribute('data-sv3-step'), 10) : null;
    }
    var modelNum = numFor('model');
    var customiseNum = numFor('customise');
    var addonNum = numFor('addon');

    if (modelNum) {
      if (sizeRadioGroup()) {
        /* Slate's Size, not a phone/frame model — see sizeChosen()/
           sizeName(). Teeinblue always arrives here with a real option
           already active (never a blank placeholder), so "done" needs no
           extra "did the customer actually touch it" gate — it can only
           ever be false if the field somehow renders with nothing active
           at all. */
        var sizeOn = sizeChosen();
        setSummary(modelNum, sizeOn ? sizeName() : 'No size selected');
      } else {
        setSummary(modelNum, s.model ? modelName() : 'Not chosen yet');
      }
    }
    if (customiseNum) {
      /* No summary text at all here any more (Scott: "remove the text
         completely... just remove that, keep the customise") — the step
         title alone stays, same as any step with nothing to summarise. */
      setSummary(customiseNum, '');
    }
    /* Which flag to read depends on which add-on this template offers —
       isPhoneProduct() and "does this product have a stand add-on" are
       mutually exclusive, same as step3Target() itself. */
    if (addonNum) {
      var addonDone = isPhoneProduct() ? state.addOn === true : state.standOn === true;
      setSummary(addonNum, addonDone ? (isPhoneProduct() ? 'Screen protector added' : 'Stand added') : 'Nothing added');
    }

    /* live-updates with the variant; cleared rather than hidden when no model is
       chosen, so the row keeps its shape and the help link stays put */
    var fit = $('#sv3-fit');
    if (fit) {
      var fitText = s.model ? ('✓ Perfect fit guaranteed for ' + modelName()) : '';
      if (fit.textContent !== fitText) fit.textContent = fitText;
    }

    /* a leftover #sv3-step2-sub from a cached build is cleared out rather than
       left stranded with stale text */
    var sub2 = $('#sv3-step2-sub');
    if (sub2) sub2.remove();

    updateBar(s);
  }

  function updateBar(s) {
    var bar = $('#sv3-bar');
    if (!bar) return;

    var price = $('.tee-price--current');
    var priceOut = $('.sv3-bar-price', bar);
    if (price && priceOut) {
      var txt = (price.textContent || '').trim();
      if (txt && priceOut.textContent !== txt) priceOut.textContent = txt;
    }

    var btn = $('.sv3-bar-btn', bar);
    var hint = $('.sv3-bar-hint', bar);
    var label, hintText, good = false, ready = false;

    if (!s.model) {
      label = 'Pick your phone to start';
      hintText = 'Free UK delivery';
    } else if (!s.layout) {
      label = 'Choose a layout';
      hintText = '✓ ' + modelName();
      good = true;
    } else if (s.photoTotal > 0 && !s.photosDone) {
      /* Same photoFields()-can-be-empty gap as the step-2 summary above —
         on a product with zero photo slots, photosDone can never be true,
         which stuck this sticky bar on "Add 1 more photo" permanently
         instead of ever reaching "Add to cart". s.photoTotal > 0 makes
         this branch only apply when there's an actual photo to ask for. */
      var left = Math.max(1, s.photoTotal - s.photoFilled);
      label = 'Add ' + left + ' more photo' + (left === 1 ? '' : 's');
      hintText = '✓ ' + modelName() + ' · ' + s.photoTotal + ' photo' + (s.photoTotal === 1 ? '' : 's');
      good = true;
    } else {
      label = state.added ? 'Added to cart ✓' : 'Add to cart';
      hintText = '✓ Ready — ships fast';
      good = true;
      ready = true;
    }

    if (btn.textContent !== label) btn.textContent = label;
    btn.classList.toggle('is-ready', ready && !state.added);
    btn.classList.toggle('is-added', ready && state.added);
    if (hint.textContent !== hintText) hint.textContent = hintText;
    hint.classList.toggle('is-good', good);

    /* keep the bar out of the way of Teeinblue dialogs (cropper, preview).
       anyTeeinblueDialogReallyOpen() (search its own comment), not a bare
       .tee-dialog presence check — a closed dialog's node lingers in the
       DOM for several seconds afterward, which left this bar hidden long
       after the customer had already closed the dialog. */
    var dialogOpen = anyTeeinblueDialogReallyOpen();
    var a = document.activeElement;
    var typing = a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA') && a.closest('#tee-artwork-form');
    document.body.classList.toggle('sv3-bar-hidden', dialogOpen || !!typing);
  }

  /* ------------------------------------------------------------------ */
  /* Boot + self-heal                                                    */
  /* ------------------------------------------------------------------ */

  function refresh() {
    /* injectStandAddon() runs before injectStepChrome() so step3Target()
       can see .sv3-stand-addon THIS pass rather than waiting a further
       150ms-debounced cycle for the MutationObserver to notice it.

       Every call goes through safe() (see its own comment above) rather
       than being called directly — the ordering and the reasoning behind
       it are unchanged, only isolated now. */
    safe(injectStandAddon);
    safe(injectStepChrome);
    safe(shortenBrandLabels);
    safe(syncBrandSelection);
    safe(bindBrandClick);
    safe(syncRadioGroupActiveState);
    safe(polishModelSelect);
    safe(bindFrameSizeClick);
    safe(addSizeDimensions);
    safe(syncAvailableProductsChoiceClass);
    safe(ensureSizeHelpBtn);
    safe(ensureSingleSizeNote);
    safe(buildSizeHelpCards);
    safe(buildFrameTypeTiles);
    safe(syncFrameTypeTiles);
    safe(buildLayoutTiles);
    safe(syncTiles);
    safe(decoratePhotoFields);
    safe(syncPreviewWatermark);
    safe(placeSectionHeads);
    safe(decoratePriceRow);
    safe(decorateKeyringBundle);
    safe(decorateAddOn);
    safe(setupStandOnSuccess);
    safe(decorateVault);
    safe(decorateConfirm);
    safe(placeTryStrip);
    safe(bindCreateCta);
    safe(bindTeeinbluePreviewDialog);
    safe(bindDialogSettleReveal);
    safe(bindSheet);
    safe(bindBar);
    safe(bindMapsFieldClicks);
    safe(placeGpxHelpBtn);
    safe(bindCropperPhotoUploads);
    safe(injectScrabbleHelp);
    safe(updateState);
  }

  var refreshTimer = null;
  function scheduleRefresh() {
    /* M12 fix (pre-launch audit) — bindConfirmGate()/gateAtc() normally only
       run inside refresh() below, which is debounced 150ms: between
       #teeAtcButton first existing and refresh() next actually running, a
       click was governed only by Teeinblue's own confirmation, not
       confirm-b, since the capture-phase gate listener wasn't bound yet.
       Both functions are idempotent — bindConfirmGate() guards on
       data-sv3-gate-bound, gateAtc() only syncs a class/attribute — so
       calling them here, synchronously, on every mutation that would
       otherwise just schedule a refresh, closes the gap with no risk of
       double-binding. Ahead of, not instead of, the debounced pass below,
       which still runs everything else. scheduleRefresh() is the one choke
       point every refresh-triggering path in this file already goes
       through, so fixing it here covers all of them, not just the form
       MutationObserver. */
    if ($('#teeAtcButton')) { bindConfirmGate(); gateAtc(); }
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () { refreshTimer = null; refresh(); }, 150);
  }

  /* Has the mockup preview painted anything at all yet? Teeinblue keeps one
     .tee-mockup-item per model in the DOM and reveals the matching one by
     giving it real width/height; every other one stays collapsed to 0x0. */
  function mockupPainted() {
    var items = document.querySelectorAll('.tee-mockup-item');
    for (var i = 0; i < items.length; i++) {
      if (items[i].getBoundingClientRect().width > 10) return true;
    }
    return false;
  }

  /* RESYNC ON LOAD, the mechanism. If the browser restored a phone-model
     selection (back/forward cache, or Teeinblue's own reselect-last-variant
     init) the <select> can show the right value while Teeinblue's Vue
     preview stays on its default shape until the customer does something
     else that happens to force a re-render.
     A REAL VALUE CHANGE, not just a re-fired event — dispatching input/
     change on the select with its value left untouched is a no-op (Vue
     diffs old vs new and sees nothing changed). Flipping to a different
     option and back — what a customer changing their mind would do —
     reliably forces Teeinblue to re-render for the real value. */
  function performModelResync() {
    var sel = getModelSelect();
    if (!sel || sel.options.length < 2) return;
    var real = sel.value;
    var decoy = null;
    for (var i = 0; i < sel.options.length; i++) {
      var v = sel.options[i].value;
      if (v && v !== 'null' && v !== real) { decoy = v; break; }
    }
    if (!decoy) return;
    sel.value = decoy;
    sel.dispatchEvent(new Event('input', { bubbles: true }));
    sel.dispatchEvent(new Event('change', { bubbles: true }));
    /* full review, finding #8 — a real wait, not the previous zero-delay
       flip straight back to `real`. Reverting immediately raced
       Teeinblue's own async mockup-load reaction to the decoy value
       (confirmed genuinely asynchronous — see this function's own header
       comment above on the ~1s warm-connection timing): if the decoy's
       asset load happened to resolve after the real value's, the visible
       mockup could settle on the wrong (decoy) shape while the <select>
       itself correctly showed the real one, right as the customer reads
       "I confirm I have checked the live preview above". 900ms matches
       the same "roughly a second" baseline scheduleModelResync() already
       measured for Teeinblue's own reaction time — same order of margin
       as its own 700ms settle buffer, applied here to the internal
       decoy-to-real flip rather than the initial restore. */
    setTimeout(function () {
      sel.value = real;
      sel.dispatchEvent(new Event('input', { bubbles: true }));
      sel.dispatchEvent(new Event('change', { bubbles: true }));
    }, 900);
  }

  /* Schedules the resync WITHOUT racing Teeinblue's own restore. Measured
     live: Teeinblue reliably repaints the correct mockup on its own within
     roughly a second of boot on a warm connection — a flat, short timer
     (the previous version fired at 300ms) can land WHILE that repaint is
     still in flight. Forcing an extra decoy-then-back value change into the
     select at that moment risks Teeinblue starting to load the decoy's
     campaign assets and racing its own in-flight restore — which can leave
     it settled on the wrong shape, i.e. can itself CAUSE the bug it's meant
     to fix on a slow connection. So instead: wait for the mockup to have
     painted SOMETHING at all (proof Teeinblue's own init has progressed
     past its initial async load), THEN wait a further settle buffer before
     touching the select — landing after, never during, Teeinblue's own
     restore. Bounded so a genuinely stuck mockup still gets the safety net. */
  function scheduleModelResync() {
    if (!modelChosen()) return;
    var waited = 0;
    var poll = setInterval(function () {
      waited += 200;
      if (mockupPainted() || waited >= 6000) {
        clearInterval(poll);
        setTimeout(performModelResync, 700);
      }
    }, 200);
  }

  /* Everything in here must only ever run ONCE per real page load — global
     document-level listeners and the two permanent 600ms timers. Split out
     of boot() so re-running boot() (see its own comment on why that now
     happens more than once per page) can't double-register any of it. */
  var globalListenersBound = false;
  function bindGlobalListenersOnce() {
    if (globalListenersBound) return;
    globalListenersBound = true;

    /* Bfcache restore (back/forward navigation) never fires DOMContentLoaded,
       so boot() above never runs again and this resync would otherwise never
       get a second chance on exactly the navigation path most likely to hand
       back a stale mockup. The listener itself, added once during the real
       load, survives in the frozen JS heap and still fires on restore. */
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) scheduleModelResync();
    });

    /* brand and model are the only controls that finish step 1 */
    ['change', 'click'].forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var t = e.target;
        /* isTrusted excludes performModelResync()'s own synthetic dispatches
           above — without it, the decoy-then-back resync it fires on every
           boot where a model already arrived pre-chosen (returning visitor,
           bfcache restore — exactly the case pickedModel exists to exclude,
           see its declaration above) would flip this true on its own and
           hand step 1 a done tick nobody earned. */
        if (!t || !t.closest || !e.isTrusted) return;
        if (t.id === 'product-option-dropdown' || t.closest('.tee-available-products')) {
          state.pickedModel = true;
        }
      });
    });

    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('#tee-artwork-form')) scheduleRefresh();
    });
    document.addEventListener('teeinblue-event-component-injected', scheduleRefresh);
    document.addEventListener('teeinblue-event-variant-changed', scheduleRefresh);
    document.addEventListener('scottsy:delivery-ready', scheduleRefresh);

    /* Catches the "Adjust Position" dialog opening/closing for
       syncPreviewWatermark() — see its own comment for why a poll, not a
       MutationObserver: the dialog is a portalled vue-js-modal, outside
       #tee-artwork-form, so the form-scoped observer below never sees it
       open, and reacting to its open/close via a broad body-level observer
       would risk the exact feedback-loop bug this file already fixed once
       this session. A cheap, harmless timer sidesteps that entirely — it
       only ever reacts to time passing, never to a DOM write, including its
       own. Runs unconditionally rather than only while watermarkEnabled():
       the dialog can open on any product, and the check inside
       syncPreviewWatermark() is what actually decides whether anything
       happens. */
    setInterval(syncPreviewWatermark, 600);

    /* B2 fallback (see syncDialogCloseGuard()'s own comment) — same
       portalled-dialog reasoning as the watermark poll above, but its own
       timer: this has to run on every product regardless of the watermark
       tag, not just the ones syncPreviewWatermark() actually does work
       for. syncPhotoStripFallback() (M13) rides the same tick — see its
       own comment. */
    setInterval(function () { syncDialogCloseGuard(); syncPhotoStripFallback(); }, 600);

    /* :has(:focus-visible) fallback (full review, finding #5) — mirrors the
       three ".tee-radio:has(:focus-visible)" CSS rules (search that string)
       with a JS class instead, so the pill/tile keyboard focus ring still
       shows on Firefox < 121 / Safari < 15.4 / Samsung Internet < 20, which
       can't evaluate :has() at all — even though :focus-visible alone is
       broadly supported there, so checking it directly on the focused
       element (rather than needing :has() to project it onto the ancestor)
       works everywhere. Without this, outline:none still strips the native
       ring but nothing ever restores it: a keyboard user tabbing through
       these pills gets zero focus indication. Delegated on document since
       these events bubble, so one pair of listeners covers every pill
       regardless of which refresh() pass built it. */
    document.addEventListener('focusin', function (e) {
      try {
        var t = e.target;
        var wrap = t && t.closest && t.closest('.tee-radio, .tee-radio--color');
        if (wrap && t.matches(':focus-visible')) wrap.classList.add('sv3-focus-visible');
      } catch (err) {}
    }, true);
    document.addEventListener('focusout', function (e) {
      try {
        var wrap = e.target && e.target.closest && e.target.closest('.tee-radio, .tee-radio--color');
        if (wrap) wrap.classList.remove('sv3-focus-visible');
      } catch (err) {}
    }, true);
  }

  /* Self-healing via a DOM attribute, same idiom as every data-sv3-*-bound
     guard elsewhere in this file — a form swapped in by a theme-editor
     section reload won't carry the attribute, so it gets its own fresh
     observer; the same form element never gets a second one stacked on
     top of it. */
  function bindFormObserver(form) {
    if (form.getAttribute('data-sv3-observed')) return;
    form.setAttribute('data-sv3-observed', '1');
    try {
      new MutationObserver(scheduleRefresh).observe(form, { childList: true, subtree: true });
    } catch (e) {}
  }

  function boot(tries) {
    var form = $('#tee-artwork-form');
    /* .tee-available-products (the two-step brand-pills UI) only exists on
       products configured with a brand-then-model picker — some products
       (e.g. the leather template) use a single flat model dropdown instead
       and never render it. Every other reference to .tee-available-products
       in this file already null-checks it, so gate on real rendered field
       content instead — true regardless of which picker style is in use. */
    var hasFields = form && form.querySelector('.tee-field');
    if (!form || !hasFields) {
      if (tries < 80) { setTimeout(function () { boot(tries + 1); }, 300); }
      return;
    }

    /* No longer a one-time-only gate (was: "if (state.built) return;") —
       see bindGlobalListenersOnce()/bindFormObserver() just above for why
       re-running the rest of this function is now safe to do more than
       once. Kept as a flag other code can still read, not as a skip. */
    state.built = true;

    document.body.classList.add('sv3-active');
    refresh();
    liftChatWidget(0);
    scheduleModelResync();
    bindFormObserver(form);
    bindGlobalListenersOnce();

    /* :has() fallback (pre-launch audit, M13) — #sv3-confirm-copy is
       server-rendered by Liquid, present in the HTML from first paint on
       non-phone templates only (photo-frame today) and absent on both
       phone-case templates, so this is a one-time check here rather than
       something that needs polling — nothing changes it after load. Mirrors
       ".sv3-page:not(:has(#sv3-confirm-copy))" in the CSS (search
       "sv3-is-phone-case") with a class on <html> instead, so the
       phone-case gallery-lock rules (stopping a customer from swiping the
       mockup slider to a different model than the one they're about to
       confirm) still apply on browsers that can't evaluate :has() at all. */
    document.documentElement.classList.toggle('sv3-is-phone-case', !document.getElementById('sv3-confirm-copy'));

    hideEmptyRelatedProducts();
  }

  /* ------------------------------------------------------------------ */
  /* Empty "Explore More Designs" heading (M6)                           */
  /* ------------------------------------------------------------------ */

  /* <product-recommendations> populates itself entirely from its OWN
     client-side fetch to Shopify's dedicated recommendations endpoint —
     confirmed live that recommendations.performed is false on a normal
     page load regardless of whether real recommendations exist, so
     Liquid has no reliable way to know in advance whether this section
     will end up empty (see related-products.liquid's own comment on
     #sv3-related-header, which this targets). This watches for the
     fetch's actual result instead.

     The observer never disconnects, on purpose: a hard "wait 5s then give
     up for good" would risk hiding the heading forever if a genuinely
     slow-but-successful fetch lands a moment after the deadline. Instead
     the timeout only ever provisionally hides it, and the observer can
     always re-show it later if cards do show up — the two can never
     leave it stuck wrong, only briefly slow to correct on an unusually
     slow connection. The manual-products path (section.settings.products
     .count > 0) never renders <product-recommendations> at all, so
     there's nothing here for it to touch. */
  function hideEmptyRelatedProducts() {
    var header = document.getElementById('sv3-related-header');
    var rec = document.querySelector('product-recommendations');
    if (!header || !rec) return;
    /* Same self-healing attribute guard as bindFormObserver() — boot() can
       now run more than once per page (theme-editor section reloads), and
       without this a repeat call would stack a second MutationObserver and
       a second 5s timeout on the same element every time. */
    if (rec.getAttribute('data-sv3-observed')) return;
    rec.setAttribute('data-sv3-observed', '1');

    function hasCards() { return !!rec.querySelector('.product-list > *'); }

    new MutationObserver(function () {
      if (hasCards() && header.style.display === 'none') header.style.display = '';
    }).observe(rec, { childList: true, subtree: true });

    setTimeout(function () {
      if (!hasCards()) header.style.display = 'none';
    }, 5000);
  }

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  onReady(function () { boot(0); });

  /* Theme-editor re-init (full review, finding #1). Shopify's Section
     Rendering API swaps a section's DOM via innerHTML-style replacement
     when it's edited in Customise — browsers never execute a <script> tag
     that arrives that way, so without this, editing anything on the
     product section (reordering blocks, a buy_buttons setting, the
     delivery-card's own Christmas-cutoff toggle) would silently strand the
     page on Teeinblue's bare form until a hard reload: boot() would simply
     never run again. shopify:section:load only ever fires inside the
     theme editor, never on the live storefront, so this is inert there.
     Safe to call boot(0) unconditionally on every firing regardless of
     which section reloaded — the retry-loop above already no-ops cleanly
     if #tee-artwork-form isn't (yet) present, and everything boot() does
     is now idempotent (bindGlobalListenersOnce/bindFormObserver/
     hideEmptyRelatedProducts's own guards) rather than gated behind a
     single-use flag. */
  document.addEventListener('shopify:section:load', function () { boot(0); });
})();
